import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import OrderFilters from './OrderFilters';
import OrderListTable from './OrderListTable';
import Pagination from '../ShipturtleProducts/Pagination';
import { 
  fetchShipturtleOrders, 
  changeOrderStatus, 
  fetchOrderCounts, 
  cancelShippingLabel, 
  fetchOrderFilterOptions, 
  bulkUpdateShipturtleOrderStatus, 
  updateBulkPaymentStatus, 
  getOrderInvoice, 
  emailOrderToVendor, 
  emailInvoiceToCustomer, 
  sendAddressVerificationEmail, 
  fetchVendorPickupAddresses, 
  updateOrderPickupAddress, 
  generateInvoices, 
  cancelInvoices, 
  bulkGenerateManifestLabel, 
  downloadFileFromUrl, 
  exportAdminOrderLogs,
  escalateShippingOrders
} from '../../api/api';
import { createExport, getExportStatus, getCurrentUserRole, searchVendors, searchCustomers, searchTags, getTerminalBanners } from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import { classifyError } from '../../utils/errorTaxonomy';
import { paymentBadge, remainingOrderValue, remainingQuantity } from '../../utils/orderLineState';
import DataFallback, { StaleDataBanner } from '../feedback/DataFallback';
import ExportOrdersModal from './ExportOrdersModal';
import ExceptionLabelsTable from './ExceptionLabelsTable';
import ManualOrdersTable from './ManualOrdersTable';
import ExportBannerModal from './ExportBannerModal';
import BulkPaymentStatusMenu from './BulkPaymentStatusMenu';
import PrescriptionUploadModal, { orderNeedsPrescription } from './PrescriptionUploadModal';
import './ManageOrders.css';

const TAB_STATUS_MAP = {
  new_orders: 'new',
  confirmed: 'confirmed',
  ready_to_ship: 'ready_to_ship',
  in_transit: 'in_transit',
  exceptions: 'exceptions',
  delivered: 'delivered',
  rto: 'rto',
  held: 'held',
  cancelled: 'cancelled',
  lost: 'lost',
  damaged: 'damaged',
  all: 'all'
};

const getApiStatusForTab = (tab, fallback = 'new') => TAB_STATUS_MAP[tab] || fallback;

const createUrlParams = (state) => {
  const params = new URLSearchParams();
  
  if (state.activeTab) {
    params.set('tab', state.activeTab);
  }
  // Only the non-default view needs to appear in the URL.
  if (state.section === 'exceptions' || state.section === 'manual') {
    params.set('section', state.section);
  }
  if (state.search?.trim()) {
    params.set('search', state.search);
  }
  if (state.page && state.page > 1) {
    params.set('page', state.page);
  }
  
  if (state.advancedFilters && Object.keys(state.advancedFilters).length > 0) {
    params.set('filters', JSON.stringify(state.advancedFilters));
  }
  
  if (state.sortState && (state.sortState.field !== 'createdAt' || state.sortState.direction !== 'desc')) {
    params.set('sort', JSON.stringify(state.sortState));
  }
  
  return params.toString() ? `?${params.toString()}` : '';
};

const parseUrlParams = (search) => {
  const params = new URLSearchParams(search || window.location.search);
  
  let advancedFilters = {};
  try {
    const filtersParam = params.get('filters');
    if (filtersParam) {
      advancedFilters = JSON.parse(filtersParam);
      if (typeof advancedFilters !== 'object' || advancedFilters === null) {
        advancedFilters = {};
      }
    }
  } catch (e) {
    console.error('Error parsing filters from URL:', e);
    advancedFilters = {};
  }

  let sortState = { field: 'createdAt', direction: 'desc' };
  try {
    const sortParam = params.get('sort');
    if (sortParam) {
      const parsedSort = JSON.parse(sortParam);
      // Validate sort state shape
      if (parsedSort && typeof parsedSort.field === 'string' && (parsedSort.direction === 'asc' || parsedSort.direction === 'desc')) {
        sortState = parsedSort;
      }
    }
  } catch (e) {
    console.error('Error parsing sort from URL:', e);
    sortState = { field: 'createdAt', direction: 'desc' };
  }

  return {
    activeTab: params.get('tab') || null,
    section: ['exceptions', 'manual'].includes(params.get('section'))
      ? params.get('section')
      : 'orders',
    search: params.get('search') || '',
    page: parseInt(params.get('page') || '1', 10),
    advancedFilters,
    sortState
  };
};

function BulkStatusModal({ isOpen, onClose, selectedOrders, activeTab, canConfirmOrders, canCancelOrders, onConfirm, isSubmitting, orders, userRole }) {
  // Only admins may move an order back to "New Order"; the option is hidden for vendors.
  const isAdmin = userRole === 'admin';
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStatus('');
      setUpdateNotes('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // An order can ship once it has a generated label OR manual shipping details
  // (manual shipping sets awb_number but never label_url). Mirrors the backend's
  // Ready-to-Ship rule, which returns error_code "shipment_required".
  const canOrderShip = (order) =>
    !!order && !!(order.labelGenerated || order.shipment?.label_url || order.shipment?.awb_number);

  const allHaveLabels = selectedOrders.every(orderId =>
    canOrderShip(orders.find(o => o.id === orderId))
  );

  // Order numbers of the selections that cannot ship yet (for the blocked-modal list)
  const ordersMissingLabelNumbers = selectedOrders
    .filter(orderId => !canOrderShip(orders.find(o => o.id === orderId)))
    .map(orderId => {
      const order = orders.find(o => o.id === orderId);
      return order?.orderNumber || orderId;
    });
  const ordersMissingLabels = ordersMissingLabelNumbers.length;

  // Determine available statuses based on current tab and permissions
  // Each status has: value, label, disabled (optional), disabledReason (optional)
  const getAvailableStatuses = () => {
    const statuses = [];

    switch (activeTab) {
      case 'new_orders':
        // From OrderListTable: Confirm Order, Hold, Cancel
        if (canConfirmOrders) statuses.push({ value: 'confirmed', label: 'Confirm Order' });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      case 'confirmed':
        // From OrderListTable: Ready to Ship, Hold, Cancel (no In Transit or Delivered here)
        statuses.push({
          value: 'ready_to_ship',
          label: 'Ready to Ship',
          disabled: !allHaveLabels,
          disabledReason: `${ordersMissingLabels} order(s) have no shipping label or manual shipping details. Generate labels or add tracking details first.`
        });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      case 'ready_to_ship':
        statuses.push({ value: 'in_transit', label: 'In Transit' });
        statuses.push({ value: 'delivered', label: 'Delivered' });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      case 'in_transit':
        statuses.push({ value: 'delivered', label: 'Delivered' });
        statuses.push({ value: 'exceptions', label: 'Exception' });
        statuses.push({ value: 'rto', label: 'RTO' });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        statuses.push({ value: 'lost', label: 'Lost' });
        statuses.push({ value: 'damaged', label: 'Damaged' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      case 'exceptions':
        // From OrderListTable: Delivered, RTO, Retry Delivery, Hold, Cancel
        statuses.push({ value: 'delivered', label: 'Delivered' });
        statuses.push({ value: 'rto', label: 'RTO' });
        statuses.push({ value: 'in_transit', label: 'Retry Delivery' });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        statuses.push({ value: 'lost', label: 'Lost' });
        statuses.push({ value: 'damaged', label: 'Damaged' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      case 'delivered':
        statuses.push({ value: 'in_transit', label: 'In Transit' });
        if (canConfirmOrders) statuses.push({ value: 'confirmed', label: 'Confirmed' });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      case 'held':
        // From OrderListTable: Move to New Orders, Move to Confirmed Order, Ready to Ship (if canConfirm), In Transit, Delivered, Cancel
        if (isAdmin) statuses.push({ value: 'new', label: 'Move to New Orders' });
        if (canConfirmOrders) {
          statuses.push({ value: 'confirmed', label: 'Move to Confirmed Order' });
          statuses.push({
            value: 'ready_to_ship',
            label: 'Ready to Ship',
            disabled: !allHaveLabels,
            disabledReason: `${ordersMissingLabels} order(s) have no shipping label or manual shipping details.`
          });
        }
        statuses.push({ value: 'in_transit', label: 'In Transit' });
        statuses.push({ value: 'delivered', label: 'Delivered' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel Order' });
        break;

      case 'rto':
        // From OrderListTable: Move to New Orders, Cancel Order
        if (isAdmin) statuses.push({ value: 'new', label: 'Move to New Orders' });
        statuses.push({ value: 'lost', label: 'Lost' });
        statuses.push({ value: 'damaged', label: 'Damaged' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel Order' });
        break;

      case 'cancelled':
        // From OrderListTable: Move to New Orders
        if (isAdmin) statuses.push({ value: 'new', label: 'Move to New Orders' });
        break;

      case 'all':
        // Show all possible statuses for 'all' tab
        if (canConfirmOrders) statuses.push({ value: 'confirmed', label: 'Confirmed' });
        statuses.push({
          value: 'ready_to_ship',
          label: 'Ready to Ship',
          disabled: !allHaveLabels,
          disabledReason: `${ordersMissingLabels} order(s) have no shipping label or manual shipping details`
        });
        statuses.push({ value: 'in_transit', label: 'In Transit' });
        statuses.push({ value: 'delivered', label: 'Delivered' });
        statuses.push({ value: 'exceptions', label: 'Exception' });
        statuses.push({ value: 'rto', label: 'RTO' });
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        statuses.push({ value: 'lost', label: 'Lost' });
        statuses.push({ value: 'damaged', label: 'Damaged' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;

      default:
        // Fallback: show common statuses
        if (isAdmin) statuses.push({ value: 'held', label: 'Hold' });
        if (canCancelOrders) statuses.push({ value: 'cancelled', label: 'Cancel' });
        break;
    }

    return statuses;
  };

  const availableStatuses = getAvailableStatuses();

  // Find the selected status object for disabled reason tooltip
  const selectedStatusObj = availableStatuses.find(s => s.value === selectedStatus);

  const handleConfirm = () => {
    if (!selectedStatus) {
      setError('Please select a status');
      return;
    }

    // Check if selected status is disabled
    const statusObj = availableStatuses.find(s => s.value === selectedStatus);
    if (statusObj && statusObj.disabled) {
      setError(statusObj.disabledReason || 'This status is not available for the selected orders.');
      return;
    }

    onConfirm({ status: selectedStatus, notes: updateNotes });
  };

  // Handle status selection - show warning if disabled status is selected
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    const statusObj = availableStatuses.find(s => s.value === value);
    if (statusObj && statusObj.disabled) {
      setError(statusObj.disabledReason || 'This status is not available.');
    } else {
      setError('');
    }
  };

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="modalHeader">
          <h3>Bulk Update Status</h3>
          <button className="closeButton" onClick={onClose} disabled={isSubmitting}>×</button>
        </div>
        <div className="modalBody">
          <p>Updating status for <strong>{selectedOrders.length}</strong> selected orders.</p>

          <div className="formGroup">
            <label>New Status <span className="required">*</span></label>
            <select
              value={selectedStatus}
              onChange={e => handleStatusChange(e.target.value)}
              disabled={isSubmitting}
              style={{ padding: '8px', fontSize: '14px', width: '100%' }}
            >
              <option value="">Select Status...</option>
              {availableStatuses.map(s => (
                <option
                  key={s.value}
                  value={s.value}
                  style={s.disabled ? { color: '#999' } : {}}
                >
                  {s.label}{s.disabled ? ' (Unavailable)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="formGroup">
            <label>Notes (Optional)</label>
            <textarea
              value={updateNotes}
              onChange={e => setUpdateNotes(e.target.value)}
              placeholder="Add internal note..."
              rows={3}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px' }}
            />
          </div>

          {error && (
            <div style={{
              color: '#dc3545',
              backgroundColor: '#fff5f5',
              padding: '10px 12px',
              borderRadius: '6px',
              marginTop: '10px',
              fontSize: '13px',
              border: '1px solid #f5c6cb'
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Warning for Ready to Ship if not all labels generated */}
          {(activeTab === 'confirmed' || activeTab === 'all') && !allHaveLabels && (
            <p style={{ fontSize: '12px', color: '#856404', backgroundColor: '#fff3cd', padding: '8px 10px', borderRadius: '4px', marginTop: '10px' }}>
              <strong>Note:</strong> {ordersMissingLabels} of {selectedOrders.length} selected order(s) have no shipping label or manual shipping details. "Ready to Ship" is disabled until every order has one.
            </p>
          )}

        </div>
        <div className="modalFooter">
          <button className="cancelButton" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button
            className="saveButton"
            onClick={handleConfirm}
            disabled={isSubmitting || !selectedStatus || (selectedStatusObj && selectedStatusObj.disabled)}
          >
            {isSubmitting ? 'Updating...' : 'Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkPickupAddressModal({
  isOpen,
  onClose,
  selectedOrders,
  orders,
  onConfirm,
  isSubmitting,
}) {
  const [pickupAddresses, setPickupAddresses] = useState([]);
  const [selectedPickupId, setSelectedPickupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedOrderObjects = orders.filter(o => selectedOrders.includes(o.id));
  const lockedSelectedOrders = selectedOrderObjects.filter(o =>
    o &&
    (o.labelGenerated || (o.shipment && (o.shipment.label_url || o.shipment.awb_number)))
  );
  const hasAnyLocked = lockedSelectedOrders.length > 0;
  const vendorIds = Array.from(
    new Set(
      selectedOrderObjects
        .map(o => o.vendorId || o.vendor?.id)
        .filter(Boolean)
    )
  );
  const hasVendorConflict = vendorIds.length > 1;
  const hasSelection = selectedOrders.length > 0;
  const effectiveVendorId = !hasVendorConflict && vendorIds.length === 1 ? vendorIds[0] : null;

  useEffect(() => {
    if (!isOpen) return;

    setError('');
    setPickupAddresses([]);
    setSelectedPickupId('');

    if (!hasSelection || hasVendorConflict || hasAnyLocked || !effectiveVendorId) {
      return;
    }

    let cancelled = false;
    const loadPickups = async () => {
      setLoading(true);
      try {
        const data = await fetchVendorPickupAddresses(effectiveVendorId);
        if (cancelled) return;
        const addresses = data.pickup_addresses || [];
        setPickupAddresses(addresses);
        if (addresses.length > 0) {
          const def = addresses.find(p => p.is_default);
          setSelectedPickupId(def?.id || addresses[0].id);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load pickup addresses');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPickups();

    return () => {
      cancelled = true;
    };
  }, [isOpen, hasSelection, hasVendorConflict, effectiveVendorId]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (hasVendorConflict || !hasSelection || hasAnyLocked) {
      return;
    }
    if (!selectedPickupId) {
      setError('Please select a pickup address.');
      return;
    }
    onConfirm({ pickupAddressId: selectedPickupId });
  };

  const selectionMessage = hasSelection
    ? `${selectedOrders.length} selected order${selectedOrders.length > 1 ? 's' : ''}`
    : 'No orders selected';

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div
        className="modalContent"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '520px' }}
      >
        <div className="modalHeader">
          <h3>Bulk Update Pickup Address</h3>
          <button className="closeButton" onClick={onClose} disabled={isSubmitting}>×</button>
        </div>
        <div className="modalBody">
          <p>
            {selectionMessage}.
          </p>

          {!hasSelection && (
            <p style={{ fontSize: '13px', color: '#6c757d' }}>
              Select one or more orders to assign a pickup address.
            </p>
          )}

          {hasVendorConflict && (
            <div
              style={{
                color: '#856404',
                backgroundColor: '#fff3cd',
                padding: '10px 12px',
                borderRadius: '6px',
                marginTop: '10px',
                fontSize: '13px',
                border: '1px solid #ffeeba',
              }}
            >
              <strong>Bulk update not available.</strong>{' '}
              Select orders belonging to the <strong>same vendor</strong> to bulk assign or update the pickup address.
            </div>
          )}

          {!hasVendorConflict && hasSelection && hasAnyLocked && (
            <div
              style={{
                color: '#856404',
                backgroundColor: '#fff3cd',
                padding: '10px 12px',
                borderRadius: '6px',
                marginTop: '10px',
                fontSize: '13px',
                border: '1px solid #ffeeba',
              }}
            >
              <strong>Pickup address locked.</strong>{' '}
              Pickup address cannot be modified for orders that already have a shipment / label generated. Deselect{' '}
              {lockedSelectedOrders.length} order{lockedSelectedOrders.length > 1 ? 's' : ''} with shipments/labels and try again.
            </div>
          )}

          {!hasVendorConflict && hasSelection && !hasAnyLocked && (
            <>
              {error && (
                <div
                  style={{
                    color: '#dc3545',
                    backgroundColor: '#fff5f5',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    marginTop: '10px',
                    fontSize: '13px',
                    border: '1px solid #f5c6cb',
                  }}
                >
                  ⚠️ {error}
                </div>
              )}

              {loading ? (
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                  <span>Loading pickup addresses…</span>
                </div>
              ) : pickupAddresses.length === 0 ? (
                <div
                  style={{
                    marginTop: '12px',
                    fontSize: '14px',
                    color: 'var(--gray-600)',
                    padding: '12px',
                    background: 'var(--gray-100)',
                    borderRadius: '6px',
                    textAlign: 'center',
                  }}
                >
                  <strong>No pickup addresses configured for this vendor.</strong>
                  <br />
                  <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                    Configure pickup addresses in vendor settings to use bulk assignment.
                  </span>
                </div>
              ) : (
                <div className="formGroup" style={{ marginTop: '12px' }}>
                  <label>Pickup Address <span className="required">*</span></label>
                  <select
                    value={selectedPickupId}
                    onChange={e => setSelectedPickupId(e.target.value)}
                    disabled={isSubmitting}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  >
                    {pickupAddresses.map(pickup => (
                      <option key={pickup.id} value={pickup.id}>
                        {(pickup.warehouse_name || 'Pickup Location') +
                          (pickup.is_default ? ' (Default)' : '')}
                      </option>
                    ))}
                  </select>
                  <small
                    style={{
                      display: 'block',
                      marginTop: '4px',
                      color: '#6c757d',
                      fontSize: '12px',
                    }}
                  >
                    The selected pickup address will be applied to all selected orders for this vendor.
                  </small>
                </div>
              )}
            </>
          )}
        </div>
        <div className="modalFooter">
          <button className="secondaryButton" onClick={onClose} disabled={isSubmitting}>
            Close
          </button>
          <button
            className="primaryButton"
            onClick={handleConfirm}
            disabled={
              isSubmitting ||
              hasVendorConflict ||
              hasAnyLocked ||
              !hasSelection ||
              (!loading && pickupAddresses.length === 0)
            }
          >
            {isSubmitting ? 'Updating…' : 'Assign / Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkInvoiceModal({
  isOpen,
  onClose,
  selectedOrders,
  orders,
  isSubmitting,
  mode = 'generate',
  onConfirm,
}) {
  const selectedOrderObjects = orders.filter(o => selectedOrders.includes(o.id));
  const hasSelection = selectedOrderObjects.length > 0;

  const eligibleOrders = selectedOrderObjects.filter(o => {
    const shipment = o.shipment;
    if (!shipment) return false;
    const hasShipmentData = Boolean(
      shipment.awb_number ||
      shipment.label_url ||
      shipment.manifest_url
    );
    const hasInvoice = !!(o.invoice && o.invoice.has_active);
    if (mode === 'generate') {
      return hasShipmentData && !hasInvoice;
    }
    if (mode === 'cancel') {
      return hasInvoice;
    }
    return false;
  });

  const eligibleCount = eligibleOrders.length;
  const ineligibleCount = selectedOrderObjects.length - eligibleCount;
  const ordersWithExistingInvoice = selectedOrderObjects.filter(
    o => o.invoice && o.invoice.has_active
  ).length;

  const handleConfirm = () => {
    if (!eligibleCount) {
      return;
    }
    const ids = eligibleOrders.map(o => o.id);
    onConfirm({ orderIds: ids, mode });
  };

  if (!isOpen) return null;

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div
        className="modalContent"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '520px' }}
      >
        <div className="modalHeader">
          <h3>Bulk Generate Invoices</h3>
          <button className="closeButton" onClick={onClose} disabled={isSubmitting}>×</button>
        </div>
        <div className="modalBody">
          {!hasSelection && (
            <p style={{ fontSize: '13px', color: '#6c757d' }}>
              Select one or more orders to generate invoices.
            </p>
          )}

          {hasSelection && mode === 'generate' && (
            <>
              <p style={{ fontSize: '14px' }}>
                You have selected <strong>{selectedOrderObjects.length}</strong> order
                {selectedOrderObjects.length > 1 ? 's' : ''}.
              </p>
              <p style={{ fontSize: '14px' }}>
                Of these, <strong>{eligibleCount}</strong> order
                {eligibleCount !== 1 ? 's' : ''} have a valid shipment / label and will be included in invoice generation.
              </p>
              {ineligibleCount > 0 && (
                <p style={{ fontSize: '13px', color: '#856404', backgroundColor: '#fff3cd', padding: '8px 10px', borderRadius: '4px', border: '1px solid #ffeeba' }}>
                  {ineligibleCount} selected order{ineligibleCount !== 1 ? 's are' : ' is'} either missing a valid shipment / label or already have an active invoice and will be skipped.
                </p>
              )}
            </>
          )}
          {hasSelection && mode === 'cancel' && (
            <>
              <p style={{ fontSize: '14px' }}>
                You have selected <strong>{selectedOrderObjects.length}</strong> order
                {selectedOrderObjects.length > 1 ? 's' : ''}.
              </p>
              <p style={{ fontSize: '14px' }}>
                Of these, <strong>{eligibleCount}</strong> order
                {eligibleCount !== 1 ? 's' : ''} currently have an active invoice and will be included in cancellation.
              </p>
              {ineligibleCount > 0 && (
                <p style={{ fontSize: '13px', color: '#856404', backgroundColor: '#fff3cd', padding: '8px 10px', borderRadius: '4px', border: '1px solid #ffeeba' }}>
                  {ineligibleCount} selected order{ineligibleCount !== 1 ? 's are' : ' is'} without an active invoice and will be skipped.
                </p>
              )}
            </>
          )}
        </div>
        <div className="modalFooter">
          <button className="secondaryButton" onClick={onClose} disabled={isSubmitting}>
            Close
          </button>
          <button
            className="primaryButton"
            onClick={handleConfirm}
            disabled={isSubmitting || !hasSelection || eligibleCount === 0}
          >
            {isSubmitting ? (mode === 'generate' ? 'Generating…' : 'Cancelling…') : (mode === 'generate' ? 'Generate Invoices' : 'Cancel Invoices')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkWeightModal({ isOpen, onClose, selectedOrdersCount, onConfirm, isSubmitting }) {
  const [weightGrams, setWeightGrams] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setWeightGrams('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const value = parseFloat(weightGrams);
    if (Number.isNaN(value) || value <= 0) {
      setError('Please enter a valid positive weight in grams.');
      return;
    }
    onConfirm({ weightGrams: value });
  };

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div
        className="modalContent"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '480px' }}
      >
        <div className="modalHeader">
          <h3>Bulk Update Package Weight</h3>
          <button className="closeButton" onClick={onClose} disabled={isSubmitting}>×</button>
        </div>
        <div className="modalBody">
          <p>Assign/update package weight for <strong>{selectedOrdersCount}</strong> selected orders.</p>
          <div className="formGroup">
            <label>Package Weight <span className="required">*</span></label>
            <input
              type="number"
              min="0"
              step="1"
              value={weightGrams}
              onChange={e => setWeightGrams(e.target.value)}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px' }}
              placeholder="Enter weight in grams"
            />
            <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '12px' }}>
              This value will be applied as <strong>Weight (grams)</strong> for all selected orders.
            </small>
          </div>
          {error && (
            <div
              style={{
                color: '#dc3545',
                backgroundColor: '#fff5f5',
                padding: '8px 10px',
                borderRadius: '6px',
                marginTop: '10px',
                fontSize: '13px',
                border: '1px solid #f5c6cb'
              }}
            >
              ⚠️ {error}
            </div>
          )}
        </div>
        <div className="modalFooter">
          <button
            className="secondaryButton"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="primaryButton"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating…' : 'Assign / Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkDimensionsModal({ isOpen, onClose, selectedOrdersCount, onConfirm, isSubmitting }) {
  const [lengthCm, setLengthCm] = useState('');
  const [breadthCm, setBreadthCm] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLengthCm('');
      setBreadthCm('');
      setHeightCm('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const l = parseFloat(lengthCm);
    const b = parseFloat(breadthCm);
    const h = parseFloat(heightCm);

    if (Number.isNaN(l) || l <= 0 || Number.isNaN(b) || b <= 0 || Number.isNaN(h) || h <= 0) {
      setError('Please enter valid positive values for all dimensions.');
      return;
    }

    onConfirm({
      lengthCm: l,
      breadthCm: b,
      heightCm: h
    });
  };

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div
        className="modalContent"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '640px' }}
      >
        <div className="modalHeader">
          <h3>Bulk Update Package Dimensions</h3>
          <button className="closeButton" onClick={onClose} disabled={isSubmitting}>×</button>
        </div>
        <div className="modalBody">
          <p>Assign/update package dimensions for <strong>{selectedOrdersCount}</strong> selected orders.</p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '12px'
            }}
          >
            <div className="formGroup">
              <label>Length (cm) <span className="required">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={lengthCm}
                onChange={e => setLengthCm(e.target.value)}
                disabled={isSubmitting}
                style={{ width: '100%', padding: '8px' }}
                placeholder="Length"
              />
            </div>
            <div className="formGroup">
              <label>Breadth (cm) <span className="required">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={breadthCm}
                onChange={e => setBreadthCm(e.target.value)}
                disabled={isSubmitting}
                style={{ width: '100%', padding: '8px' }}
                placeholder="Breadth"
              />
            </div>
            <div className="formGroup">
              <label>Height (cm) <span className="required">*</span></label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={heightCm}
                onChange={e => setHeightCm(e.target.value)}
                disabled={isSubmitting}
                style={{ width: '100%', padding: '8px' }}
                placeholder="Height"
              />
            </div>
          </div>
          <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontSize: '12px' }}>
            These values will be applied as <strong>Length, Breadth, Height (cm)</strong> for all selected orders.
          </small>
          {error && (
            <div
              style={{
                color: '#dc3545',
                backgroundColor: '#fff5f5',
                padding: '8px 10px',
                borderRadius: '6px',
                marginTop: '10px',
                fontSize: '13px',
                border: '1px solid #f5c6cb'
              }}
            >
              ⚠️ {error}
            </div>
          )}
        </div>
        <div className="modalFooter">
          <button
            className="secondaryButton"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="primaryButton"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating…' : 'Assign / Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * BulkEmailModal – supports two modes:
 *   mode = 'vendor'   → Email Orders to Vendors
 *   mode = 'customer' → Email Invoices to Customers
 *
 * For 'customer' mode the modal pre-filters orders that have an active invoice
 * so the user sees exactly how many emails will actually be sent.
 */
function BulkEmailModal({
  isOpen,
  onClose,
  selectedOrders,
  orders,
  isSubmitting,
  mode = 'vendor', // 'vendor' | 'customer'
  onConfirm,
  // For select-all mode we pass down the count of matching orders
  selectAllMode = false,
  totalSelectAllCount = 0,
}) {
  const selectedOrderObjects = orders.filter(o => selectedOrders.includes(o.id));
  const hasSelection = selectAllMode || selectedOrderObjects.length > 0;

  // Vendor mode: every selected order is eligible (backend skips orders with no vendor email)
  // Customer / invoice mode: only orders that have an active invoice are eligible for sending
  let eligibleOrders = selectedOrderObjects;
  let ineligibleOrders = [];
  let ineligibleReason = '';

  if (mode === 'customer' && !selectAllMode) {
    eligibleOrders = selectedOrderObjects.filter(o => o.invoice && o.invoice.has_active);
    ineligibleOrders = selectedOrderObjects.filter(o => !(o.invoice && o.invoice.has_active));
    ineligibleReason = 'no active invoice generated';
  }

  const eligibleCount = selectAllMode ? totalSelectAllCount : eligibleOrders.length;
  const ineligibleCount = selectAllMode ? 0 : ineligibleOrders.length;

  const handleConfirm = () => {
    if (!hasSelection) return;
    if (eligibleCount === 0) return;
    onConfirm({ mode });
  };

  if (!isOpen) return null;

  const title = mode === 'vendor' ? 'Email Orders to Vendors' : 'Email Invoices to Customers';
  const icon = mode === 'vendor' ? '📦' : '🧾';
  const confirmLabel = mode === 'vendor' ? 'Send Vendor Emails' : 'Send Invoice Emails';

  // Nice description of what each mode does
  const modeDescription = mode === 'vendor'
    ? 'Order details will be sent by email to the vendor assigned to each selected order.'
    : 'The generated invoice PDF will be sent by email to the customer of each selected order.';

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div
        className="modalContent"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '520px' }}
      >
        <div className="modalHeader">
          <h3>{icon} {title}</h3>
          <button className="closeButton" onClick={onClose} disabled={isSubmitting}>×</button>
        </div>

        <div className="modalBody">
          <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '12px' }}>
            {modeDescription}
          </p>

          {!hasSelection && (
            <p style={{ fontSize: '13px', color: '#6c757d' }}>
              Select one or more orders to send emails.
            </p>
          )}

          {hasSelection && selectAllMode && (
            <p style={{ fontSize: '14px' }}>
              You have selected <strong>all matching orders ({totalSelectAllCount})</strong>.
              {mode === 'customer' && (
                <span style={{ display: 'block', marginTop: '6px', fontSize: '13px', color: '#856404' }}>
                  Note: Orders without an active invoice will be skipped automatically by the server.
                </span>
              )}
            </p>
          )}

          {hasSelection && !selectAllMode && (
            <>
              <p style={{ fontSize: '14px' }}>
                You have selected <strong>{selectedOrderObjects.length}</strong> order
                {selectedOrderObjects.length > 1 ? 's' : ''}.
              </p>

              {mode === 'customer' && (
                <>
                  <p style={{ fontSize: '14px' }}>
                    Of these, <strong>{eligibleCount}</strong> order
                    {eligibleCount !== 1 ? 's' : ''} have an active invoice and will receive the email.
                  </p>
                  {ineligibleCount > 0 && (
                    <p style={{
                      fontSize: '13px',
                      color: '#856404',
                      backgroundColor: '#fff3cd',
                      padding: '8px 10px',
                      borderRadius: '4px',
                      border: '1px solid #ffeeba',
                      marginTop: '8px',
                    }}>
                      ⚠️ {ineligibleCount} order{ineligibleCount !== 1 ? 's are' : ' is'} missing an active invoice ({ineligibleReason}) and will be skipped.
                      Generate invoices for those orders first, then re-send.
                    </p>
                  )}
                </>
              )}

              {mode === 'vendor' && (
                <p style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>
                  Orders without a vendor email configured will be skipped automatically.
                </p>
              )}
            </>
          )}

          {eligibleCount === 0 && hasSelection && !selectAllMode && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              borderRadius: '6px',
              backgroundColor: '#fff5f5',
              border: '1px solid #f5c6cb',
              color: '#dc3545',
              fontSize: '13px',
            }}>
              {mode === 'customer'
                ? '⚠️ None of the selected orders have an active invoice. Generate invoices first.'
                : '⚠️ No eligible orders to send emails for.'}
            </div>
          )}
        </div>

        <div className="modalFooter">
          <button className="secondaryButton" onClick={onClose} disabled={isSubmitting}>
            Close
          </button>
          <button
            className="primaryButton"
            onClick={handleConfirm}
            disabled={isSubmitting || !hasSelection || eligibleCount === 0}
          >
            {isSubmitting ? 'Sending…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MultiBrandConflictModal({ isOpen, onClose, brands }) {
  if (!isOpen) return null;

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="modalHeader">
          <h3>Multiple Brands Selected</h3>
          <button className="closeButton" onClick={onClose}>×</button>
        </div>
        <div className="modalBody">
          <p>Selected orders belong to multiple brands: <strong>{brands.join(', ')}</strong>. Please select orders from a single brand.</p>
        </div>
        <div className="modalFooter">
          <button className="primaryButton" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

function BulkManifestValidationModal({ isOpen, onClose, validationErrors, processed, onProceed }) {
  if (!isOpen) return null;

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modalHeader">
          <h3>Validation Errors</h3>
          <button className="closeButton" onClick={onClose}>×</button>
        </div>
        <div className="modalBody" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <p>The following orders have validation errors and were skipped:</p>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {validationErrors.map((err, idx) => (
              <li key={idx} style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '6px' }}>
                <div style={{ fontWeight: '600' }}>Order: {err.order_number}</div>
                <ul style={{ fontSize: '13px', color: '#dc3545', marginTop: '4px' }}>
                  {err.errors.map((msg, i) => <li key={i}>{msg}</li>)}
                </ul>
              </li>
            ))}
          </ul>
        </div>
        <div className="modalFooter">
          {processed > 0 ? (
            <>
              <button className="secondaryButton" onClick={onClose}>Cancel</button>
              <button className="primaryButton" onClick={onProceed}>Proceed with {processed} valid orders</button>
            </>
          ) : (
            <button className="primaryButton" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}


function ManageOrders() {
  const userTypeRaw = useSelector(state => state.user?.user_type);
  const userId = useSelector(state => state.user?.id);

  const location = useLocation();
  const navigate = useNavigate();
  const urlStateRef = useRef({ hasInitialized: false });

  const mapUserTypeToShipturtleRole = (role) => {
    if (!role) return 'vendor';
    if (role === 'vendor' || role === 'admin') return role;
    if (role === 'appuser' || role === 'vendor_admin') return 'vendor';
    if (role === 'employee') return 'admin';
    return 'vendor';
  };

  const [userRole, setUserRole] = useState(mapUserTypeToShipturtleRole(userTypeRaw));
  const [roleLoading, setRoleLoading] = useState(true);


  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState(null);
  // When the rows on screen were last confirmed current. Drives the stale notice
  // shown when a refresh cannot reach the server but the list is still usable.
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const getInitialUrlState = useCallback(() => {
    if (urlStateRef.current.hasInitialized) {
      return urlStateRef.current.state;
    }
    
    const urlParams = parseUrlParams(location.search);
    urlStateRef.current.state = urlParams;
    urlStateRef.current.hasInitialized = true;
    return urlParams;
  }, [location.search]);
  
  const urlState = getInitialUrlState();
  const [activeTab, setActiveTab] = useState(urlState.activeTab);
  // 'orders' (the status tabs) or 'exceptions' (return/replacement labels).
  const [section, setSection] = useState(urlState.section || 'orders');
  const [orderCounts, setOrderCounts] = useState(null);

  // If the current user is a vendor, ensure we never stay on a hidden tab
  // (New Orders and the admin-only Held bucket).
  useEffect(() => {
    if (userRole === 'vendor' && (activeTab === 'new_orders' || activeTab === 'held')) {
      setActiveTab('confirmed');
      setCurrentPage(1);
    }
  }, [userRole, activeTab]);

  // Same for the section: `?section=exceptions` is read straight off the URL, so
  // without this a non-admin who lands on that link would render the exception
  // list and watch every request 403. The endpoints are IsAdmin-gated, so this
  // is about not showing a broken screen, not about access itself.
  useEffect(() => {
    if (userRole && userRole !== 'admin' && section !== 'orders') {
      setSection('orders');
    }
  }, [userRole, section]);

  const [filters, setFilters] = useState({
    globalSearch: urlState.search || '',
    orderStatus: 'all',
    paymentStatus: 'all',
    payoutStatus: 'all',
    dateRange: 'all'
  });

  const [currentPage, setCurrentPage] = useState(urlState.page || 1);
  const [itemsPerPage] = useState(100); // Fixed at 100, matching Shipturtle
  const [serverPagination, setServerPagination] = useState(null);

  // Debounced filters for API calls (to prevent API call on every keystroke)
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const debounceTimerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAllMode, setSelectAllMode] = useState(false);  // Track if "select all matching" is active
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportJobId, setExportJobId] = useState(null);
  // Set when a polled export finishes; rendered as a click-to-download banner
  // (auto-opening would be popup-blocked outside a user gesture).
  const [exportReady, setExportReady] = useState(null);
  const [showExportBannerModal, setShowExportBannerModal] = useState(false);
  const [exportBanners, setExportBanners] = useState([]);
  const [sortState, setSortState] = useState(urlState.sortState);
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkWeightModal, setShowBulkWeightModal] = useState(false);
  const [showBulkDimensionsModal, setShowBulkDimensionsModal] = useState(false);
  const [showBulkPickupModal, setShowBulkPickupModal] = useState(false);
  const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState(false);
  const [bulkInvoiceMode, setBulkInvoiceMode] = useState('generate');
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailMode, setBulkEmailMode] = useState('vendor'); // 'vendor' | 'customer'
  const [isBulkEmailSending, setIsBulkEmailSending] = useState(false);
  const [isBulkDimensionsUpdating, setIsBulkDimensionsUpdating] = useState(false);
  const [isBulkPickupUpdating, setIsBulkPickupUpdating] = useState(false);
  const [isBulkInvoiceGenerating, setIsBulkInvoiceGenerating] = useState(false);
  const [isBulkManifestLabelGenerating, setIsBulkManifestLabelGenerating] = useState(false);
  const [showMultiBrandModal, setShowMultiBrandModal] = useState(false);
  // Opened when a one-order bulk label run is stopped for a missing prescription.
  const [prescriptionModalOrderId, setPrescriptionModalOrderId] = useState(null);
  const [multiBrandConflictData, setMultiBrandConflictData] = useState([]);
  const [showValidationErrorsModal, setShowValidationErrorsModal] = useState(false);
  const [validationErrorsData, setValidationErrorsData] = useState([]);
  const [processedOrdersCount, setProcessedOrdersCount] = useState(0);
  const [pendingLabelUrl, setPendingLabelUrl] = useState(null);
  const [pendingManifestUrl, setPendingManifestUrl] = useState(null);
  const [editableDimensions, setEditableDimensions] = useState({});
  const [selectedProzoChannels, setSelectedProzoChannels] = useState({});
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isExportingAdminLogs, setIsExportingAdminLogs] = useState(false);
  const pollingIntervalRef = useRef(null);
  const trackingPollRef = useRef(null);
  const trackingPollParamsRef = useRef(null);
  const [showAdvancedFiltersModal, setShowAdvancedFiltersModal] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(urlState.advancedFilters || {});
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    vendors: [],
    tags: [],
    orderStatuses: [],
    trackingStatuses: [],
    financialStatuses: [],
    rtoSubStatuses: [],
  });
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);
  const [localFilters, setLocalFilters] = useState({});
  const [tagInput, setTagInput] = useState('');

  // Shipping escalation state
  const [isEscalating, setIsEscalating] = useState(false);

  const buildBackendFiltersForSelectAll = useCallback(() => {
    const currentStatus = getApiStatusForTab(activeTab);
    const search = debouncedFilters.globalSearch?.trim() || '';

    const filtersPayload = {};

    // Tab status + global search
    if (currentStatus && currentStatus !== 'all') {
      filtersPayload.status = currentStatus;
    }
    if (search) {
      filtersPayload.search = search;
    }

    // Advanced filters (normalize keys to backend expectations)
    if (advancedFilters.orderNumber) {
      filtersPayload.order_number = advancedFilters.orderNumber;
    }

    // Carrier: backend expects carrier_ids (list) (or carrierIds)
    if (Array.isArray(advancedFilters.carrierIds) && advancedFilters.carrierIds.length > 0) {
      filtersPayload.carrier_ids = advancedFilters.carrierIds;
    } else if (advancedFilters.carrierId) {
      filtersPayload.carrier_ids = [advancedFilters.carrierId];
    }

    if (advancedFilters.labelGenerated !== undefined) {
      filtersPayload.label_generated = advancedFilters.labelGenerated;
    }

    if (advancedFilters.shipmentStatus) {
      filtersPayload.shipment_status = advancedFilters.shipmentStatus;
    }

    if (advancedFilters.paymentStatus) {
      filtersPayload.payment_status = advancedFilters.paymentStatus;
    }

    if (Array.isArray(advancedFilters.tags) && advancedFilters.tags.length > 0) {
      filtersPayload.tags = advancedFilters.tags;
    }

    if (advancedFilters.customer) {
      filtersPayload.customer = advancedFilters.customer;
    }

    if (advancedFilters.awb) {
      filtersPayload.awb = advancedFilters.awb;
    }

    if (advancedFilters.dateFrom) {
      filtersPayload.date_from = advancedFilters.dateFrom;
    }
    if (advancedFilters.dateTo) {
      filtersPayload.date_to = advancedFilters.dateTo;
    }

    if (advancedFilters.deliveredFrom) {
      filtersPayload.delivered_from = advancedFilters.deliveredFrom;
    }
    if (advancedFilters.deliveredTo) {
      filtersPayload.delivered_to = advancedFilters.deliveredTo;
    }

    if (Array.isArray(advancedFilters.vendorIds) && advancedFilters.vendorIds.length > 0) {
      filtersPayload.vendor_ids = advancedFilters.vendorIds;
    }

    if (Array.isArray(advancedFilters.rtoSubStatuses) && advancedFilters.rtoSubStatuses.length > 0) {
      filtersPayload.rto_sub_statuses = advancedFilters.rtoSubStatuses;
    }

    // Remove undefined/null/empty-string values but keep valid false/0 values
    Object.keys(filtersPayload).forEach((key) => {
      if (filtersPayload[key] === undefined || filtersPayload[key] === null || filtersPayload[key] === '') {
        delete filtersPayload[key];
      }
      if (Array.isArray(filtersPayload[key]) && filtersPayload[key].length === 0) {
        delete filtersPayload[key];
      }
    });

    return filtersPayload;
  }, [activeTab, debouncedFilters.globalSearch, advancedFilters]);

  // Vendor search state
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const vendorDropdownRef = useRef(null);
  const vendorSearchTimeoutRef = useRef(null);

  // Customer search state (local suggestions from loaded orders)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customerDropdownRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  // Tag search state (API-based search)
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const tagDropdownRef = useRef(null);
  const tagSearchTimeoutRef = useRef(null);

  // RTO sub-status multi-select dropdown state
  const [showRtoSubStatusDropdown, setShowRtoSubStatusDropdown] = useState(false);
  const rtoSubStatusDropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        if (cancelled) {
          return;
        }
        setUserRole(roleData?.role);
      } catch (err) {
        console.error('Error fetching Shipturtle user role:', err);
        if (!cancelled) {
          setUserRole((prev) => prev || mapUserTypeToShipturtleRole(userTypeRaw));
        }
      } finally {
        if (!cancelled) {
          setRoleLoading(false);
        }
      }
    };

    fetchRole();

    return () => {
      cancelled = true;
    };
  }, [userTypeRaw]);

  // Set activeTab after role is loaded
  useEffect(() => {
    if (!roleLoading && activeTab === null) {
      setActiveTab(userRole === 'admin' ? 'new_orders' : 'confirmed');
    }
  }, [roleLoading, userRole, activeTab]);

  useEffect(() => {
    if (roleLoading || activeTab === null) {
      return;
    }

    const urlParams = createUrlParams({
      activeTab,
      section,
      search: debouncedFilters.globalSearch,
      page: currentPage,
      advancedFilters,
      sortState,
    });

    if (location.search !== urlParams) {
      navigate({ search: urlParams }, { replace: true });
    }
  }, [activeTab, section, debouncedFilters.globalSearch, currentPage, advancedFilters, sortState, roleLoading, navigate, location.search]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const params = parseUrlParams(location.search);
    
    // Only update local state if it differs from URL to prevent infinite loops
    if (params.activeTab !== null && params.activeTab !== activeTab) {
      setActiveTab(params.activeTab);
    }
    if (params.section !== section) {
      setSection(params.section);
    }
    if (params.search !== filters.globalSearch) {
      setFilters(prev => ({ ...prev, globalSearch: params.search }));
    }
    if (params.page !== currentPage) {
      setCurrentPage(params.page);
    }
    if (JSON.stringify(params.advancedFilters) !== JSON.stringify(advancedFilters)) {
      setAdvancedFilters(params.advancedFilters);
    }
    if (JSON.stringify(params.sortState) !== JSON.stringify(sortState)) {
      setSortState(params.sortState);
    }
  }, [location.search]);

  const isVendor = userRole === 'vendor';
  const isAdmin = userRole === 'admin';
  const canConfirmOrders = isAdmin;
  const canCancelOrders = isAdmin;
  const canEditShipToAddresses = isAdmin;
  const isReadOnlyAuditor = userTypeRaw === 'read_only_auditor';

  // Function to fetch order counts
  const fetchCounts = async () => {
    try {
      const countsResponse = await fetchOrderCounts(isVendor ? userId : null);
      if (countsResponse.success && countsResponse.status_counts) {
        setOrderCounts(countsResponse.status_counts);
      }
    } catch (error) {
      console.error('Error fetching order counts:', error);
      // Don't set error state, just log it - counts are not critical
    }
  };

  // Fetch order counts on component mount
  useEffect(() => {
    fetchCounts();
  }, [isVendor, userId]);

  // Debounce filter changes for text inputs (wait 500ms after user stops typing)
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced update
    debounceTimerRef.current = setTimeout(() => {
      const searchChanged = filters.globalSearch !== debouncedFilters.globalSearch;
      setDebouncedFilters(filters);
      // Reset to page 1 when search changes
      if (searchChanged) {
        setCurrentPage(1);
      }
    }, 500); // 500ms delay

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);

  // Clear selected orders when tab changes (page reset is handled in onClick)
  useEffect(() => {
    if (previousTabRef.current !== null && previousTabRef.current !== activeTab && activeTab !== null) {
      setSelectedOrders([]);
      // Refresh counts when tab changes to update tab badges smoothly
      fetchCounts();
    }
  }, [activeTab, isVendor, userId]);

  // Track if this is the initial load or a tab change (should show full loading)
  const isInitialLoadRef = useRef(true);
  const previousTabRef = useRef(null);
  const previousPageRef = useRef(null);
  const previousSearchRef = useRef(null);
  const previousAdvancedFiltersRef = useRef(null);
  const previousSortStateRef = useRef(null);

  const handleSort = useCallback((field) => {
    setSortState(prev => {
      const newDirection = prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc';
      return { field, direction: newDirection };
    });
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    // Don't fetch orders until activeTab is set
    if (activeTab === null) {
      return;
    }

    // Determine if this is the very first initial load (show full loading) vs tab/page changes (show table loading)
    const isTabChange = previousTabRef.current !== null && previousTabRef.current !== activeTab;
    // Only show full loading on the very first load. Tab changes and pagination use table loading.
    const shouldShowFullLoading = isInitialLoadRef.current;

    const fetchOrders = async () => {
      try {
        // Use full loading only for the very first initial load
        if (shouldShowFullLoading) {
          setLoading(true);
        } else {
          // For tab changes and pagination, use page loading state (keeps UI visible)
          setIsPageLoading(true);
        }
        setError(null);

        const apiStatus = getApiStatusForTab(activeTab, 'new');

        // Calculate offset based on current page and items per page
        const offset = (currentPage - 1) * itemsPerPage;
        const limit = itemsPerPage;

        // Get search term from debounced filters
        const searchTerm = debouncedFilters.globalSearch?.trim() || null;

        // Fetch orders from API with pagination parameters, search, and filters
        const response = await fetchShipturtleOrders(apiStatus, limit, offset, searchTerm, advancedFilters, sortState);

        if (response.success && response.orders) {
          // Map API response to component format
          const mappedOrders = response.orders.map(order => mapApiOrderToComponent(order));
          setOrders(mappedOrders);

          // Store server pagination metadata
          setServerPagination({
            count: response.count || 0,
            limit: response.limit || limit,
            offset: response.offset || offset,
            total_count: response.count || 0,
            total_pages: response.count ? Math.ceil(response.count / limit) : 1,
            page: currentPage,
            page_size: limit,
          });
          setLastLoadedAt(Date.now());
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (err) {
        // Keep the classified description rather than flattening it to a
        // string: it carries the copy to show, whether a retry is worth
        // offering, and which of the several causes this was.
        setError(err && err.classified ? err.classified : classifyError(err));
        console.error('Error fetching orders:', err);
      } finally {
        if (shouldShowFullLoading) {
          setLoading(false);
        } else {
          setIsPageLoading(false);
        }
        // Mark initial load as complete and update refs
        isInitialLoadRef.current = false;
        previousTabRef.current = activeTab;
        previousPageRef.current = currentPage;
        previousSearchRef.current = debouncedFilters.globalSearch?.trim() || null;
        previousAdvancedFiltersRef.current = JSON.stringify(advancedFilters || {});
        previousSortStateRef.current = JSON.stringify(sortState || {});
      }
    };

    // Only fetch if tab changed OR page changed OR search changed OR it's the initial load
    // Note: When tab changes, page is reset to 1, but React batches updates so we only fetch once
    const tabChanged = previousTabRef.current !== null && previousTabRef.current !== activeTab;
    const pageChanged = previousPageRef.current !== null && previousPageRef.current !== currentPage;
    const currentSearch = debouncedFilters.globalSearch?.trim() || null;
    const searchChanged = previousSearchRef.current !== currentSearch;

    const currentAdvancedFilters = JSON.stringify(advancedFilters || {});
    const advancedFiltersChanged = previousAdvancedFiltersRef.current !== currentAdvancedFilters;

    const currentSortState = JSON.stringify(sortState || {});
    const sortChanged = previousSortStateRef.current !== currentSortState;

    if (isInitialLoadRef.current || tabChanged || pageChanged || searchChanged || advancedFiltersChanged || sortChanged) {
      fetchOrders();
    }

    // Listen for status changes from other parts of the app (e.g., OrderDetails)
    const onOrderStatusChanged = (e) => {
      try {
        // Refresh orders and counts when a status changed event occurs
        // Use page loading for status change refreshes
        setIsPageLoading(true);
        fetchOrders().finally(() => setIsPageLoading(false));
        fetchCounts();
      } catch (err) {
        console.error('Error handling orderStatusChanged event', err);
        setIsPageLoading(false);
      }
    };

    window.addEventListener('shipturtle:orderStatusChanged', onOrderStatusChanged);

    return () => {
      window.removeEventListener('shipturtle:orderStatusChanged', onOrderStatusChanged);
    };
  }, [isVendor, userId, activeTab, currentPage, itemsPerPage, debouncedFilters, advancedFilters, sortState]);

  /**
   * Retry after a load that could not reach the server.
   *
   * Reuses the refresh path the status-change listener already drives, rather
   * than duplicating the fetch: this effect closes over the current tab, page
   * and filters, so the retry repeats exactly the request that failed.
   */
  const retryFetchOrders = useCallback(() => {
    window.dispatchEvent(new CustomEvent('shipturtle:orderStatusChanged'));
  }, []);

  // Keep poll params fresh in a ref — no API calls, no re-renders
  useEffect(() => {
    trackingPollParamsRef.current = {
      activeTab,
      currentPage,
      itemsPerPage,
      searchTerm: debouncedFilters.globalSearch?.trim() || null,
      advancedFilters,
    };
  }, [activeTab, currentPage, itemsPerPage, debouncedFilters.globalSearch, advancedFilters]);

  // Poll tracking links on tab/window focus — fires the instant the user returns from Django admin.
  // A 60s fallback interval also runs so idle pages eventually stay in sync.
  // Both are set up ONCE on mount (empty deps) and read fresh params from the ref.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isPolling = false; // guard: prevent overlapping requests

    const poll = async () => {
      if (isPolling) return;
      const params = trackingPollParamsRef.current;
      if (!params) return;
      isPolling = true;
      try {
        const apiStatus = getApiStatusForTab(params.activeTab, 'new');
        const offset = (params.currentPage - 1) * params.itemsPerPage;
        const response = await fetchShipturtleOrders(
          apiStatus, params.itemsPerPage, offset, params.searchTerm, params.advancedFilters
        );
        if (response.success && response.orders) {
          const _stageNorm = { first: 'first_follow_up', second: 'second_follow_up', exception: 'exception', penalty: 'penalty', none: 'none' };
          const updatedMap = {};
          response.orders.forEach(apiOrder => {
            updatedMap[apiOrder.id] = {
              trackingLink: apiOrder.tracking_link || '',
              followUpStage: _stageNorm[apiOrder.follow_up_stage] || apiOrder.follow_up_stage || 'none',
              exceptionActive: apiOrder.exception_active || false,
              exceptionNote: apiOrder.exception_note || '',
              penaltySent: apiOrder.follow_up_stage === 'penalty',
              penaltyPercentage: apiOrder.penalty_percentage || 0,
            };
          });
          setOrders(prev => {
            let changed = false;
            const next = prev.map(order => {
              const u = updatedMap[order.id];
              if (!u) return order;
              if (
                u.trackingLink !== order.trackingLink ||
                u.followUpStage !== order.followUpStage ||
                u.exceptionActive !== order.exceptionActive ||
                u.penaltySent !== order.penaltySent
              ) {
                changed = true;
                return { ...order, ...u };
              }
              return order;
            });
            return changed ? next : prev;
          });
        }
      } catch (_) {}
      isPolling = false;
    };

    // Fire immediately when the user switches back to this browser tab
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll();
    };
    // Also fire when the window regains focus (e.g. alt-tab from another app)
    const onFocus = () => poll();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    // Fallback: still run every 60s for cases where the user never leaves the tab
    const intervalId = setInterval(poll, 60000);
    trackingPollRef.current = intervalId;

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      clearInterval(intervalId);
      trackingPollRef.current = null;
    };
  }, []); // empty deps — set up once, reads fresh values from ref

  // Load filter options when modal opens
  useEffect(() => {
    const loadFilterOptions = async () => {
      if (showAdvancedFiltersModal && filterOptions.carriers.length === 0) {
        setIsLoadingFilterOptions(true);
        try {
          const response = await fetchOrderFilterOptions();
          if (response.success) {
            setFilterOptions({
              carriers: response.carriers || [],
              vendors: response.vendors || [],
              tags: response.tags || [],
              orderStatuses: response.order_statuses || [],
              trackingStatuses: response.tracking_statuses || [],
              financialStatuses: response.financial_statuses || [],
              rtoSubStatuses: response.rto_sub_statuses || [],
            });
          }
        } catch (error) {
          console.error('Error loading filter options:', error);
          showToast('Failed to load filter options', 'error');
        } finally {
          setIsLoadingFilterOptions(false);
        }
      }

      // Sync local filters with applied filters when modal opens
      if (showAdvancedFiltersModal) {
        setLocalFilters({ ...advancedFilters });

        const appliedCustomer = advancedFilters?.customer || '';
        if (appliedCustomer) {
          setCustomerSearchTerm(appliedCustomer);
          setSelectedCustomer({ id: appliedCustomer, display: appliedCustomer });
        } else {
          setCustomerSearchTerm('');
          setSelectedCustomer(null);
        }
      }
    };

    loadFilterOptions();
  }, [showAdvancedFiltersModal]);

  // Handle advanced filter changes
  const handleAdvancedFiltersApply = useCallback(() => {
    // Remove empty/default values before applying
    const cleanFilters = {};
    Object.keys(localFilters).forEach(key => {
      const value = localFilters[key];
      if (value !== '' && value !== 'all' && !(Array.isArray(value) && value.length === 0)) {
        cleanFilters[key] = value;
      }
    });
    setAdvancedFilters(cleanFilters);
    setCurrentPage(1);
    setShowAdvancedFiltersModal(false);
  }, [localFilters]);

  const handleFilterInputChange = useCallback((field, value) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleMultiSelectChange = useCallback((field, value, checked) => {
    setLocalFilters(prev => {
      const currentValues = prev[field] || [];
      if (checked) {
        return { ...prev, [field]: [...currentValues, value] };
      } else {
        return { ...prev, [field]: currentValues.filter(v => v !== value) };
      }
    });
  }, []);

  // Tag search functionality (API-based search)
  const performTagSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 1) {
      setTagSuggestions([]);
      return;
    }

    try {
      const result = await searchTags(searchTerm);
      if (result.success && result.tags) {
        setTagSuggestions(result.tags);
      } else {
        setTagSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching tags:', error);
      setTagSuggestions([]);
    }
  }, []);

  const handleTagSearchChange = useCallback((e) => {
    const value = e.target.value;
    setTagSearchTerm(value);
    setShowTagDropdown(true);

    if (tagSearchTimeoutRef.current) {
      clearTimeout(tagSearchTimeoutRef.current);
    }

    if (!value || value.trim().length === 0) {
      setTagSuggestions([]);
      setShowTagDropdown(false);
      return;
    }

    tagSearchTimeoutRef.current = setTimeout(() => {
      performTagSearch(value);
    }, 300);
  }, [performTagSearch]);

  const handleTagSelect = useCallback((tag) => {
    // Check if tag is already selected
    const isSelected = selectedTags.includes(tag);

    if (isSelected) {
      // Remove tag
      const newSelectedTags = selectedTags.filter(t => t !== tag);
      setSelectedTags(newSelectedTags);
      setLocalFilters(prev => ({
        ...prev,
        tags: newSelectedTags
      }));
    } else {
      // Add tag
      const newSelectedTags = [...selectedTags, tag];
      setSelectedTags(newSelectedTags);
      setLocalFilters(prev => ({
        ...prev,
        tags: newSelectedTags
      }));
    }

    // Clear search but keep dropdown open for multi-select
    setTagSearchTerm('');
    setTagSuggestions([]);
  }, [selectedTags]);

  const handleRemoveTagChip = useCallback((tagToRemove) => {
    const newSelectedTags = selectedTags.filter(t => t !== tagToRemove);
    setSelectedTags(newSelectedTags);
    setLocalFilters(prev => ({
      ...prev,
      tags: newSelectedTags
    }));
  }, [selectedTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
      if (rtoSubStatusDropdownRef.current && !rtoSubStatusDropdownRef.current.contains(event.target)) {
        setShowRtoSubStatusDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && !(localFilters.tags || []).includes(tagInput.trim())) {
      setLocalFilters(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  }, [tagInput, localFilters.tags]);

  const handleRemoveTag = useCallback((tagToRemove) => {
    setLocalFilters(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  }, []);

  const handleRemoveAdvancedFilter = useCallback((filterKey) => {
    const newFilters = { ...advancedFilters };
    delete newFilters[filterKey];
    setAdvancedFilters(newFilters);
    setCurrentPage(1);
  }, [advancedFilters]);

  const handleClearAllFilters = useCallback(() => {
    // Clear all filter states
    setAdvancedFilters({});
    setLocalFilters({});
    setSelectedVendors([]);
    setSelectedCustomer(null);
    setSelectedTags([]);
    setVendorSearchTerm('');
    setCustomerSearchTerm('');
    setTagSearchTerm('');
    setCurrentPage(1);
  }, []);

  // Vendor search functionality
  const performVendorSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setVendorSuggestions([]);
      setVendorLoading(false);
      return;
    }

    try {
      setVendorLoading(true);
      const response = await searchVendors(searchTerm, 20);
      const results = response.vendors || response.results || [];

      const searchLower = searchTerm.toLowerCase();

      // Filter: Only show vendors where the DISPLAYED name contains the search term
      const filtered = results.filter(vendor => {
        const displayName = (vendor.brand_name || vendor.company_name || '').toLowerCase();
        return displayName.includes(searchLower);
      });

      // Sort: Exact matches first, then starts-with, then contains
      const sorted = filtered.sort((a, b) => {
        const aName = (a.brand_name || a.company_name || '').toLowerCase();
        const bName = (b.brand_name || b.company_name || '').toLowerCase();

        const aExact = aName === searchLower;
        const bExact = bName === searchLower;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        const aStarts = aName.startsWith(searchLower);
        const bStarts = bName.startsWith(searchLower);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        return aName.localeCompare(bName);
      });

      setVendorSuggestions(sorted);
      setVendorLoading(false);
    } catch (error) {
      console.error('Error searching vendors:', error);
      setVendorSuggestions([]);
      setVendorLoading(false);
    }
  }, []);

  const handleVendorSearchChange = useCallback((e) => {
    const value = e.target.value;
    setVendorSearchTerm(value);
    setShowVendorDropdown(true);

    // Clear previous timeout
    if (vendorSearchTimeoutRef.current) {
      clearTimeout(vendorSearchTimeoutRef.current);
    }

    // If user cleared the input
    if (!value || value.trim().length === 0) {
      setVendorSuggestions([]);
      setShowVendorDropdown(false);
      return;
    }

    // Debounce API call
    setVendorLoading(true);
    vendorSearchTimeoutRef.current = setTimeout(() => {
      performVendorSearch(value);
    }, 300);
  }, [performVendorSearch]);

  const handleVendorSelect = useCallback((vendor) => {
    // Check if vendor is already selected
    const isSelected = selectedVendors.some(v => v.id === vendor.id);

    if (isSelected) {
      // Remove vendor
      const newSelectedVendors = selectedVendors.filter(v => v.id !== vendor.id);
      setSelectedVendors(newSelectedVendors);
      setLocalFilters(prev => ({
        ...prev,
        vendorIds: newSelectedVendors.map(v => v.id)
      }));
    } else {
      // Add vendor
      const newSelectedVendors = [...selectedVendors, vendor];
      setSelectedVendors(newSelectedVendors);
      setLocalFilters(prev => ({
        ...prev,
        vendorIds: newSelectedVendors.map(v => v.id)
      }));
    }

    // Keep dropdown open for multi-select
    setVendorSearchTerm('');
    setVendorSuggestions([]);
  }, [selectedVendors]);

  const handleRemoveVendor = useCallback((vendorId) => {
    const newSelectedVendors = selectedVendors.filter(v => v.id !== vendorId);
    setSelectedVendors(newSelectedVendors);
    setLocalFilters(prev => ({
      ...prev,
      vendorIds: newSelectedVendors.map(v => v.id)
    }));
  }, [selectedVendors]);

  // Customer search functionality (API-based search)
  const performCustomerSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCustomerSuggestions([]);
      return;
    }

    try {
      const result = await searchCustomers(searchTerm);
      if (result.success && result.customers) {
        // Format customers for display
        const formatted = result.customers.map(customer => ({
          id: customer.id,
          name: customer.full_name || `${customer.first_name} ${customer.last_name}`.trim() || 'Anonymous',
          email: customer.email || '',
          phone: customer.phone || '',
          display: customer.full_name || customer.email || customer.phone || 'Anonymous',
        }));
        setCustomerSuggestions(formatted);
      } else {
        setCustomerSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSuggestions([]);
    }
  }, []);

  const handleCustomerSearchChange = useCallback((e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    setShowCustomerDropdown(true);

    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    if (!value || value.trim().length === 0) {
      setSelectedCustomer(null);
      setCustomerSuggestions([]);
      setShowCustomerDropdown(false);
      setLocalFilters(prev => {
        const next = { ...prev };
        delete next.customer;
        return next;
      });
      return;
    }

    customerSearchTimeoutRef.current = setTimeout(() => {
      performCustomerSearch(value);
    }, 300);
  }, [performCustomerSearch]);

  const handleCustomerSelect = useCallback((customer) => {
    if (!customer) {
      setSelectedCustomer(null);
      setCustomerSearchTerm('');
      setCustomerSuggestions([]);
      setShowCustomerDropdown(false);
      setLocalFilters(prev => {
        const next = { ...prev };
        delete next.customer;
        return next;
      });
      return;
    }

    setSelectedCustomer(customer);
    setCustomerSearchTerm(customer.display);
    setShowCustomerDropdown(false);
    setCustomerSuggestions([]);

    // Keep backend contract: send a single string for customer filter.
    // Prefer email if available, else phone, else name.
    const customerValue = customer.email || customer.phone || customer.name;
    setLocalFilters(prev => ({ ...prev, customer: customerValue }));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close vendor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Highlight matching text in vendor names
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    // Escape the term: an unescaped "(" or "+" typed into the search box
    // would otherwise throw on RegExp construction.
    const term = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Split keeps the captured match, so odd indices are always the matches.
    // Don't use regex.test() to decide: the /g flag makes it stateful via
    // lastIndex and it would flip between true/false across calls.
    const parts = text.split(new RegExp(`(${term})`, 'gi'));

    // Wrapped in a single element because .dropdownItem is display:flex; a
    // bare array would make each fragment its own flex item and spread the
    // name across the row.
    return (
      <span className="highlightText">
        {parts.map((part, i) =>
          part === '' ? null : i % 2 === 1
            ? <strong key={i}>{part}</strong>
            : <React.Fragment key={i}>{part}</React.Fragment>
        )}
      </span>
    );
  };

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    Object.keys(localFilters).forEach(key => {
      const value = localFilters[key];
      if (value !== '' && value !== 'all' && !(Array.isArray(value) && value.length === 0)) {
        count++;
      }
    });
    return count;
  }, [localFilters]);

  // Count active filters for badge
  const activeAdvancedFilterCount = Object.keys(advancedFilters).filter(key => {
    const value = advancedFilters[key];
    return value !== '' && value !== 'all' && !(Array.isArray(value) && value.length === 0);
  }).length;

  // Apply client-side filters (payment, payout) to the orders returned from server
  // Note: Server-side handles status filtering, pagination, and search
  const filteredOrders = useMemo(() => orders.filter(order => {
    const matchesOrderStatus = filters.orderStatus === 'all' || order.orderStatus === filters.orderStatus;
    const matchesPaymentStatus = filters.paymentStatus === 'all' || order.paymentStatus === filters.paymentStatus;
    const matchesPayoutStatus = filters.payoutStatus === 'all' || order.payoutStatus === filters.payoutStatus;
    return matchesOrderStatus && matchesPaymentStatus && matchesPayoutStatus;
  }), [orders, filters.orderStatus, filters.paymentStatus, filters.payoutStatus]);

  // Use server-side pagination metadata if available, otherwise fall back to client-side calculation
  const totalPages = serverPagination
    ? serverPagination.total_pages
    : Math.ceil(filteredOrders.length / itemsPerPage);

  const totalCount = serverPagination
    ? serverPagination.total_count
    : filteredOrders.length;

  const startIndex = serverPagination
    ? serverPagination.offset
    : (currentPage - 1) * itemsPerPage;

  const endIndex = startIndex + filteredOrders.length;

  // Orders are already paginated by the server, so use filteredOrders directly
  const paginatedOrders = filteredOrders;

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Reset to page 1 when filters change (especially search)
    if (newFilters.globalSearch !== undefined) {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Helper function to refresh orders with current pagination
  const refreshOrdersWithPagination = async (statusOverride = null) => {
    const apiStatus = statusOverride || getApiStatusForTab(activeTab, 'new');
    const offset = (currentPage - 1) * itemsPerPage;
    const limit = itemsPerPage;
    const searchTerm = debouncedFilters.globalSearch?.trim() || null;

    // Use page loading for refresh operations (not full loading)
    setIsPageLoading(true);
    try {
      const response = await fetchShipturtleOrders(apiStatus, limit, offset, searchTerm, advancedFilters, sortState);
      if (response.success && response.orders) {
        const mappedOrders = response.orders.map(order => mapApiOrderToComponent(order));
        setOrders(mappedOrders);

        // Update server pagination metadata
        setServerPagination({
          count: response.count || 0,
          limit: response.limit || limit,
          offset: response.offset || offset,
          total_count: response.count || 0,
          total_pages: response.count ? Math.ceil(response.count / limit) : 1,
          page: currentPage,
          page_size: limit,
        });
      }
      return response;
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleOrderSelect = (orderId, isSelected) => {
    setSelectedOrders(prev => {
      if (isSelected) {
        return [...prev, orderId];
      } else {
        return prev.filter(id => id !== orderId);
      }
    });
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      // Add current page orders to selection (merge, don't replace)
      const currentPageIds = paginatedOrders.map(order => order.id);
      setSelectedOrders(prev => {
        const newSelection = [...prev];
        currentPageIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    } else {
      // Remove only current page orders from selection
      const currentPageIds = paginatedOrders.map(order => order.id);
      setSelectedOrders(prev => prev.filter(id => !currentPageIds.includes(id)));
    }
  };

  const handleSelectAllFiltered = () => {
    setSelectedOrders(filteredOrders.map(order => order.id));
    setSelectAllMode(false);  // Disable select all mode when selecting filtered
  };

  const handleSelectAllGlobal = () => {
    // Enable "Select All Matching" mode
    setSelectAllMode(true);
    setSelectedOrders([]);  // Clear individual selections
  };

  const handleUnselectAll = () => {
    setSelectedOrders([]);
    setSelectAllMode(false);  // Disable select all mode
  };

  // Bulk cancel labels for selected orders
  const handleCancelLabels = async () => {
    const orderIds = selectedOrders.filter(Boolean);
    if (!selectAllMode && orderIds.length === 0) return;

    const totalCount = selectAllMode ? (serverPagination?.total_count || 0) : orderIds.length;
    const confirmMessage = selectAllMode
      ? `Cancel shipping labels for all ${totalCount} matching orders? This action cannot be undone.`
      : orderIds.length === 1
        ? 'Are you sure you want to cancel this shipping label? This action cannot be undone.'
        : `Cancel shipping labels for ${orderIds.length} selected orders? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const currentFilters = buildBackendFiltersForSelectAll();

      const response = await cancelShippingLabel(orderIds, selectAllMode, currentFilters);

      if (response && response.success) {
        const cancelledCount = response.success_count || orderIds.length;
        showToast({
          type: 'success',
          message: selectAllMode
            ? `Cancelled labels for ${cancelledCount} matching order${cancelledCount > 1 ? 's' : ''}`
            : `Cancelled labels for ${orderIds.length} order${orderIds.length > 1 ? 's' : ''}`,
          duration: 5000
        });

        // Refresh orders and counts
        await refreshOrdersWithPagination();
        await fetchCounts();

        // Clear selection and close menu
        setSelectedOrders([]);
        setSelectAllMode(false);
        setShowBulkActionsMenu(false);
      } else {
        showToast({
          type: 'error',
          message: response?.message || 'Failed to cancel shipping label(s)'
        });
      }
    } catch (error) {
      console.error('Error cancelling label(s):', error);
      showToast({
        type: 'error',
        message: error.message || 'Failed to cancel shipping label(s)'
      });
    }
  };

  // Export admin order audit logs
  const handleExportAdminOrderLogs = async () => {
    setIsExportingAdminLogs(true);
    try {
      await exportAdminOrderLogs();
      showToast({
        type: 'success',
        message: 'Order audit logs exported successfully',
        duration: 3000
      });
    } catch (error) {
      console.error('Error exporting admin order logs:', error);
      showToast({
        type: 'error',
        message: 'Failed to export order audit logs'
      });
    } finally {
      setIsExportingAdminLogs(false);
    }
  };

  const handleEscalation = async () => {
    if (!isAdmin) return;
    if (activeTab !== 'ready_to_ship' && activeTab !== 'in_transit') return;

    setIsEscalating(true);
    try {
      const response = await escalateShippingOrders(activeTab);

      if (response?.success === false) {
        showToast({
          type: 'error',
          message: response?.message || 'Escalation failed.',
          duration: 5000,
        });
        return;
      }

      const totalOrders = Number(response?.total_orders) || 0;
      const emailed = response?.email_sent === true;

      if (totalOrders > 0 && !emailed) {
        showToast({
          type: 'error',
          message:
            response?.message ||
            'Excel was prepared but email could not be sent. Please retry.',
          duration: 7000,
        });
        return;
      }

      if (emailed && totalOrders > 0) {
        showToast({
          type: 'success',
          message:
            response?.message ||
            `Escalation email sent successfully (${totalOrders} order${totalOrders !== 1 ? 's' : ''}).`,
          duration: 5000,
        });
        return;
      }

      showToast({
        type: 'success',
        message:
          response?.message ||
          'No orders matched this escalation. Nothing to send.',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error escalating shipping orders:', error);
      showToast({
        type: 'error',
        message: error.message || 'Escalation failed. Please try again.'
      });
    } finally {
      setIsEscalating(false);
    }
  };

  const EscalationLoadingOverlay = () => {
    if (!isEscalating) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '40px',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <p style={{
            fontSize: '16px',
            color: '#333',
            margin: 0,
            fontWeight: '500'
          }}>
            Escalating...
          </p>
        </div>
      </div>
    );
  };

  const handleBulkStatusUpdateConfirm = async ({ status, notes }) => {
    if (!selectAllMode && selectedOrders.length === 0) return;

    // Only admins may move orders back to "New Order" status from any bucket.
    if (!isAdmin && status === 'new') {
      showToast({ type: 'error', message: 'You do not have permission to move orders back to New Order.' });
      return;
    }

    // Only admins may place orders in the Held bucket.
    if (!isAdmin && status === 'held') {
      showToast({ type: 'error', message: 'You do not have permission to move orders to Held status.' });
      return;
    }

    // Only admins may cancel orders.
    if (!canCancelOrders && status === 'cancelled') {
      showToast({ type: 'error', message: 'You do not have permission to cancel orders.' });
      return;
    }

    setIsBulkUpdating(true);
    try {
      const currentFilters = buildBackendFiltersForSelectAll();

      const response = await bulkUpdateShipturtleOrderStatus(
        selectedOrders,
        selectAllMode,
        currentFilters,
        status,
        notes
      );

      if (response && response.success) {
        const updatedCount = response.updated_count || selectedOrders.length;

        // The bulk endpoint partial-succeeds: orders without a dispatchable
        // shipment are skipped individually rather than failing the batch.
        const blocked = (response.results || []).filter(r => r?.error_code === 'shipment_required');

        if (blocked.length > 0) {
          const listed = blocked.slice(0, 5).map(r => r.order_number || r.order_id).join(', ');
          const more = blocked.length > 5 ? ` and ${blocked.length - 5} more` : '';
          showToast({
            type: 'error',
            message: `Label not generated for ${listed}${more}. Generate label or add manual shipping details before marking Ready to Ship.`,
            duration: 6000,
          });
        }

        if (updatedCount > 0) {
          showToast({
            type: 'success',
            message: selectAllMode
              ? `Successfully updated ${updatedCount} matching orders to ${status}`
              : `Successfully updated ${updatedCount} orders to ${status}`,
            duration: 5000
          });
        }

        // Refresh orders and counts
        await refreshOrdersWithPagination();
        await fetchCounts();

        // Close modal and clear selection
        setShowBulkStatusModal(false);
        setSelectedOrders([]);
        setSelectAllMode(false);
      } else {
        showToast({
          type: 'error',
          message: response?.message || 'Failed to update orders'
        });
      }
    } catch (error) {
      console.error('Error updating bulk status:', error);
      showToast({
        type: 'error',
        message: error.message || 'Failed to update user status'
      });
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Bulk Payment Status Update Handler
  // Refactored: Uses unified payment status update API with syncShopify enabled by default.
  // Supports both individual selection and "select all matching" modes.
  const handleBulkPaymentUpdate = async (status) => {
    const orderIds = selectedOrders.filter(Boolean);
    if (!selectAllMode && orderIds.length === 0) return;

    // Using global page loading to block interaction
    setIsPageLoading(true);

    try {
      const currentFilters = buildBackendFiltersForSelectAll();

      // syncShopify is set to true by default for better user experience
      const response = await updateBulkPaymentStatus(orderIds, status, true, '', selectAllMode, currentFilters);

      if (response && response.success) {
        const successCount = response.updated_count || orderIds.length;
        const shopifySyncCount = response.shopify_sync_count || 0;

        showToast({
          type: 'success',
          message: selectAllMode
            ? `Updated ${successCount} matching order${successCount > 1 ? 's' : ''}. Shopify synced: ${shopifySyncCount}`
            : `Updated ${successCount} order${successCount > 1 ? 's' : ''}. Shopify synced: ${shopifySyncCount}`,
          duration: 5000
        });

        // Refresh orders and counts
        await refreshOrdersWithPagination();
        await fetchCounts();

        // Clear selection
        setSelectedOrders([]);
        setSelectAllMode(false);
      } else {
        showToast({
          type: 'error',
          message: response?.message || 'Failed to update payment status'
        });
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      showToast({
        type: 'error',
        message: error.message || 'Failed to update payment status'
      });
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleDimensionChange = (orderId, field, value) => {
    setEditableDimensions(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [field]: value
      }
    }));
  };

  const handleProzoChannelChange = (orderId, channelName) => {
    setSelectedProzoChannels(prev => ({
      ...prev,
      [orderId]: channelName
    }));
  };

  const handleBulkWeightConfirm = ({ weightGrams }) => {
    if (!selectAllMode && selectedOrders.length === 0) {
      return;
    }

    const targetOrderIds = selectAllMode
      ? paginatedOrders.map(o => o.id)
      : selectedOrders;

    setEditableDimensions(prev => {
      const next = { ...prev };
      targetOrderIds.forEach(id => {
        next[id] = {
          ...(next[id] || {}),
          weight: String(weightGrams)
        };
      });
      return next;
    });

    setShowBulkWeightModal(false);
    setSelectedOrders([]);
    setSelectAllMode(false);
    showToast({
      type: 'success',
      message: `Updated weight for ${targetOrderIds.length} order${targetOrderIds.length > 1 ? 's' : ''}.`,
      duration: 4000
    });
  };

  const handleBulkDimensionsConfirm = ({ lengthCm, breadthCm, heightCm }) => {
    if (!selectAllMode && selectedOrders.length === 0) {
      return;
    }

    const targetOrderIds = selectAllMode
      ? paginatedOrders.map(o => o.id)
      : selectedOrders;

    setIsBulkDimensionsUpdating(true);
    setEditableDimensions(prev => {
      const next = { ...prev };
      targetOrderIds.forEach(id => {
        next[id] = {
          ...(next[id] || {}),
          length: String(lengthCm),
          breadth: String(breadthCm),
          height: String(heightCm)
        };
      });
      return next;
    });
    setIsBulkDimensionsUpdating(false);
    setShowBulkDimensionsModal(false);
    setSelectedOrders([]);
    setSelectAllMode(false);
    showToast({
      type: 'success',
      message: `Updated dimensions for ${targetOrderIds.length} order${targetOrderIds.length > 1 ? 's' : ''}.`,
      duration: 4000
    });
  };

  const handleBulkPickupConfirm = async ({ pickupAddressId }) => {
    if (selectedOrders.length === 0) {
      return;
    }

    const targetOrderIds = selectedOrders.slice();
    const targetOrders = orders.filter(o => targetOrderIds.includes(o.id));
    const lockedOrders = targetOrders.filter(o =>
      o &&
      (o.labelGenerated || (o.shipment && (o.shipment.label_url || o.shipment.awb_number)))
    );

    if (lockedOrders.length > 0) {
      showToast({
        type: 'error',
        message: `Pickup address cannot be updated for ${lockedOrders.length} order${lockedOrders.length > 1 ? 's' : ''} that already have a shipment / label generated. Deselect them and try again.`,
        duration: 6000,
      });
      return;
    }

    const vendorIds = Array.from(
      new Set(
        targetOrders
          .map(o => o.vendorId || o.vendor?.id)
          .filter(Boolean)
      )
    );

    if (vendorIds.length !== 1) {
      showToast({
        type: 'error',
        message: 'Select orders belonging to the same vendor to bulk assign the pickup address.',
      });
      return;
    }

    setIsBulkPickupUpdating(true);
    try {
      const results = await Promise.all(
        targetOrderIds.map(id => updateOrderPickupAddress(id, pickupAddressId))
      );

      const successCount = results.filter(r => r && r.success).length;
      const failedCount = targetOrderIds.length - successCount;

      if (successCount > 0) {
        showToast({
          type: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? `Updated pickup address for ${successCount} order(s). ${failedCount} failed.`
              : `Updated pickup address for ${successCount} order(s).`,
          duration: 5000,
        });
      } else {
        showToast({
          type: 'error',
          message: 'Failed to update pickup address for the selected orders.',
          duration: 5000,
        });
      }

      await refreshOrdersWithPagination();
      await fetchCounts();

      setSelectedOrders([]);
      setSelectAllMode(false);
      setShowBulkPickupModal(false);
    } catch (error) {
      console.error('Error updating pickup address in bulk:', error);
      showToast({
        type: 'error',
        message: error.message || 'Failed to update pickup address for selected orders.',
        duration: 5000,
      });
    } finally {
      setIsBulkPickupUpdating(false);
    }
  };

  const handleBulkInvoiceConfirm = async ({ orderIds, mode }) => {
    const ids = Array.isArray(orderIds) ? orderIds : [];
    if (ids.length === 0) {
      showToast({
        type: 'error',
        message:
          mode === 'generate'
            ? 'No eligible orders with valid shipments/labels to generate invoices.'
            : 'No eligible orders with active invoices to cancel.',
      });
      return;
    }

    setIsBulkInvoiceGenerating(true);
    try {
      const result =
        mode === 'generate'
          ? await generateInvoices(ids)
          : await cancelInvoices(ids);

      const successCount = result?.success_count || 0;
      const failedCount = result?.failed_count || 0;

      if (successCount > 0) {
        showToast({
          type: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? mode === 'generate'
                ? `Generated invoices for ${successCount} order(s). ${failedCount} failed.`
                : `Cancelled invoices for ${successCount} order(s). ${failedCount} failed.`
              : mode === 'generate'
                ? `Generated invoices for ${successCount} order(s).`
                : `Cancelled invoices for ${successCount} order(s).`,
          duration: 5000,
        });
      } else {
        showToast({
          type: 'error',
          message:
            result?.error ||
            (mode === 'generate'
              ? 'Failed to generate invoices for selected orders.'
              : 'Failed to cancel invoices for selected orders.'),
          duration: 5000,
        });
      }

      await refreshOrdersWithPagination();
      await fetchCounts();

      setSelectedOrders([]);
      setSelectAllMode(false);
      setShowBulkInvoiceModal(false);
    } catch (error) {
      console.error(
        mode === 'generate'
          ? 'Error generating invoices in bulk:'
          : 'Error cancelling invoices in bulk:',
        error
      );
      showToast({
        type: 'error',
        message:
          error.message ||
          (mode === 'generate'
            ? 'Failed to generate invoices for selected orders.'
            : 'Failed to cancel invoices for selected orders.'),
        duration: 5000,
      });
    } finally {
      setIsBulkInvoiceGenerating(false);
    }
  };

  // Bulk email handler — mirrors handleBulkInvoiceConfirm pattern
  const handleBulkEmail = async ({ mode }) => {
    const filters = buildBackendFiltersForSelectAll();
    const orderIds = selectAllMode ? [] : selectedOrders.slice();

    setIsBulkEmailSending(true);
    try {
      const result = mode === 'vendor'
        ? await emailOrderToVendor(orderIds, selectAllMode, filters)
        : await emailInvoiceToCustomer(orderIds, selectAllMode, filters);

      const sentCount = result?.total_sent ?? result?.success_count ?? 0;
      const failedCount = result?.total_failed ?? result?.failed_count ?? 0;
      const skippedCount = result?.total_skipped ?? 0;

      const entityLabel = mode === 'vendor' ? 'vendor' : 'customer';

      if (sentCount > 0) {
        const extra = [];
        if (failedCount > 0) extra.push(`${failedCount} failed`);
        if (skippedCount > 0) extra.push(`${skippedCount} skipped`);
        showToast({
          type: failedCount > 0 ? 'warning' : 'success',
          message: `Sent ${sentCount} email${sentCount !== 1 ? 's' : ''} to ${entityLabel}${extra.length ? ` (${extra.join(', ')})` : ''}.`,
          duration: 5000,
        });
      } else if (skippedCount > 0 && failedCount === 0) {
        // All skipped — likely no invoices / no vendor emails
        const reason = mode === 'customer'
          ? 'None of the selected orders have an active invoice.'
          : 'None of the selected orders have a vendor email configured.';
        showToast({ type: 'warning', message: reason, duration: 6000 });
      } else {
        const fallback = result?.error ||
          (mode === 'vendor'
            ? 'Failed to send order emails to vendors.'
            : 'Failed to send invoice emails to customers.');
        showToast({ type: 'error', message: fallback, duration: 5000 });
      }

      setSelectedOrders([]);
      setSelectAllMode(false);
      setShowBulkEmailModal(false);
    } catch (error) {
      console.error(`Error sending bulk ${mode} emails:`, error);
      showToast({
        type: 'error',
        message: error.message || `Failed to send emails to ${mode === 'vendor' ? 'vendors' : 'customers'}.`,
        duration: 5000,
      });
    } finally {
      setIsBulkEmailSending(false);
    }
  };

  const handleBulkGenerateManifestLabel = async (proceedWithValid = false) => {
    const filters = buildBackendFiltersForSelectAll();
    const orderIds = selectAllMode ? [] : selectedOrders.slice();

    const selectedOrderObjects = !selectAllMode ? orders.filter(o => selectedOrders.includes(o.id)) : [];

    if (!proceedWithValid && !selectAllMode) {
      const vendorIds = Array.from(
        new Set(
          selectedOrderObjects
            .map(o => o.vendorId || o.vendor?.id)
            .filter(Boolean)
        )
      );
      if (vendorIds.length > 1) {
        showToast({
          type: 'error',
          message: "Labels can't be generated because orders selected belong to different vendors.",
          duration: 5000,
        });
        setShowBulkActionsMenu(false);
        return;
      }

      const ordersWithExistingLabels = selectedOrderObjects.filter(order => {
        return order && (order.labelGenerated || !!order.shipment?.label_url);
      });
      if (ordersWithExistingLabels.length > 0) {
        const orderNumbers = ordersWithExistingLabels.map(o => o.orderNumber || o.id).join(', ');
        showToast({
          type: 'error',
          message: `Label already exists for: ${orderNumbers}. Deselect orders with existing labels to proceed.`,
          duration: 6000,
        });
        setShowBulkActionsMenu(false);
        return;
      }

      // Prescription orders need their prescription before any label. In
      // select-all mode the server reports these per order instead.
      const ordersMissingPrescription = selectedOrderObjects.filter(orderNeedsPrescription);
      if (ordersMissingPrescription.length > 0) {
        setShowBulkActionsMenu(false);
        if (selectedOrderObjects.length === 1) {
          setPrescriptionModalOrderId(ordersMissingPrescription[0].id);
          return;
        }
        const orderNumbers = ordersMissingPrescription.map(o => o.orderNumber || o.id).join(', ');
        showToast({
          type: 'error',
          message: `Prescription required for: ${orderNumbers}. Add it (PDF or link) from the order row, or deselect these orders to proceed.`,
          duration: 7000,
        });
        return;
      }

    }

    setIsBulkManifestLabelGenerating(true);
    
    try {
      let channelName = '';
      if (!selectAllMode && selectedOrders.length > 0) {
        const firstOrderId = selectedOrders[0];
        channelName = selectedProzoChannels[firstOrderId] || '';
        console.log('[BULK MANIFEST] Using channel from local state:', channelName, 'for order', firstOrderId);
      }

      const orderDimensions = {};
      if (!selectAllMode && selectedOrderObjects.length > 0) {
        selectedOrderObjects.forEach(order => {
          const editableDims = editableDimensions[order.id] || {};
          
          const length = parseFloat(editableDims.length || 0);
          const breadth = parseFloat(editableDims.breadth || 0);
          const height = parseFloat(editableDims.height || 0);
          const weight = parseFloat(editableDims.weight || 0);
          
          if (length > 0 && breadth > 0 && height > 0 && weight > 0) {
            orderDimensions[order.id] = {
              length,
              breadth,
              height,
              weight 
            };
          }
        });
      }

      const response = await bulkGenerateManifestLabel(orderIds, selectAllMode, filters, channelName, orderDimensions);

      if (response.success === false) {
        if (response.status === 400 && response.data?.error === 'multi_brand_conflict') {
          setMultiBrandConflictData(response.data.brands || []);
          setShowMultiBrandModal(true);
          setShowBulkActionsMenu(false);
          return;
        }

        if (response.status === 400 && response.data?.label_generation_errors?.length > 0) {
          const errorDetails = response.data.label_generation_errors.map(err => 
            `${err.order_number}: ${err.error || err.message}`
          ).join('\n');
          showToast({
            type: 'error',
            message: `Label generation failed:\n${errorDetails}`,
            duration: 8000,
          });
          setShowBulkActionsMenu(false);
          return;
        }

        if (response.data?.validation_errors?.length > 0) {
          setValidationErrorsData(response.data.validation_errors);
          setProcessedOrdersCount(response.data.processed_count || 0);
          
          if (response.data.label_pdf_url) {
             setPendingLabelUrl(response.data.label_pdf_url);
          }
          if (response.data.manifest_pdf_url) {
             setPendingManifestUrl(response.data.manifest_pdf_url);
          }
          
          setShowValidationErrorsModal(true);
          setShowBulkActionsMenu(false); 
          return;
        }

        showToast({
          type: 'error',
          message: response.data?.error || response.data?.message || 'Failed to generate manifest and label.',
          duration: 5000,
        });
        setShowBulkActionsMenu(false);
        return;
      }

      // Handle response errors and warnings
      const labelGenerationErrors = response.data?.label_generation_errors || [];
      const manifestError = response.data?.manifest_error;

      if (labelGenerationErrors.length > 0 || manifestError) {
        let warningMessage = '';
        
        if (labelGenerationErrors.length > 0) {
          const errorDetails = labelGenerationErrors.map(err => 
            `${err.order_number}: ${err.error || err.message}`
          ).join('\n');
          warningMessage += `Label generation failed for some orders:\n${errorDetails}`;
          
          if (manifestError) {
            warningMessage += '\n\n';
          }
        }
        
        if (manifestError) {
          const manifestOrders = manifestError.orders?.map(o => o.order_number).join(', ') || 'some orders';
          const manifestReason = manifestError.reason || 'unknown reason';
          warningMessage += `Manifest generation failed for: ${manifestOrders} (${manifestReason})`;
        }

        showToast({
          type: 'warning',
          message: warningMessage,
          duration: 8000,
        });
      } else {
        showToast({
          type: 'success',
          message: 'Manifest and labels generated successfully!',
          duration: 5000,
        });
      }

      setSelectedOrders([]);
      setSelectAllMode(false);
      setShowBulkActionsMenu(false);

      if (response.label_pdf_url) {
        await downloadFileFromUrl(response.label_pdf_url, 'bulk_labels.pdf');
      }
      if (response.manifest_pdf_url) {
        await downloadFileFromUrl(response.manifest_pdf_url, 'bulk_manifests.pdf');
      }

      await refreshOrdersWithPagination();
      await fetchCounts();

    } catch (error) {
      console.error('Error in handleBulkGenerateManifestLabel:', error);
      showToast({
        type: 'error',
        message: error.message || 'An unexpected error occurred during manifest & label generation.',
        duration: 5000,
      });
      setShowBulkActionsMenu(false);
    } finally {
      setIsBulkManifestLabelGenerating(false);
    }
  };

  const handleProceedWithValidOrders = async () => {
    if (pendingLabelUrl) {
      await downloadFileFromUrl(pendingLabelUrl, 'bulk_labels.pdf');
    }
    if (pendingManifestUrl) {
      await downloadFileFromUrl(pendingManifestUrl, 'bulk_manifests.pdf');
    }

    setShowValidationErrorsModal(false);
    setValidationErrorsData([]);
    setPendingLabelUrl(null);
    setPendingManifestUrl(null);
    setSelectedOrders([]);
    setSelectAllMode(false);

    await refreshOrdersWithPagination();
    await fetchCounts();
    
    showToast({
      type: 'success',
      message: `${processedOrdersCount} order(s) processed successfully.`,
      duration: 5000,
    });
  };

  // Export functionality
  const handleExportConfirm = async ({ type, format, columns }) => {
    setIsExporting(true);
    setExportError('');

    try {
      const exportFilters = buildBackendFiltersForSelectAll();

      const sortConfig = {
        field: sortState.field,
        direction: sortState.direction,
      };

      const response = await createExport(
        type,
        format,
        selectedOrders,
        selectAllMode,
        exportFilters,
        sortConfig,
        columns
      );

      const _showBannerModalAfterExport = () => {
        getTerminalBanners().then((banners) => {
          if (banners && banners.length > 0) {
            setExportBanners(banners);
            setShowExportBannerModal(true);
          }
        }).catch(() => {});
      };

      // Exports always run in the background now and are delivered by email —
      // the backend no longer returns a ready-to-download response.
      if (response.status === 'queued') {
        setExportJobId(response.jobId);
        showToast({
          type: 'success',
          message: `Export queued. We'll email it to ${response.email || 'your registered email'} when it's ready — this can take a few minutes for large exports.`,
          duration: 8000,
        });
        setSelectedOrders([]);
        setSelectAllMode(false);
        setShowExportModal(false);
        _showBannerModalAfterExport();

        // Poll so small exports can offer an immediate download link.
        if (response.jobId) {
          startPollingExportJob(response.jobId);
        }
      } else {
        throw new Error(response.message || 'Unknown export status');
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to create export. Please try again.';
      setExportError(errorMessage);
      showToast({
        type: 'error',
        message: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Poll export job status
  const startPollingExportJob = (jobId) => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const maxAttempts = 60; // Poll for up to 5 minutes (5s intervals)
    let attempts = 0;

    pollingIntervalRef.current = setInterval(async () => {
      attempts++;

      try {
        const status = await getExportStatus(jobId);

        if (status.status === 'ready' && status.downloadUrl) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          // Don't auto-open: this runs outside a user gesture and would be
          // popup-blocked. Surface a link the user can click instead.
          setExportReady({ url: status.downloadUrl, filename: status.filename || 'export' });
          showToast({
            type: 'success',
            message: `Export ready — ${status.filename || 'your file'} is available to download.`,
            duration: 8000,
          });
          setSelectedOrders([]);
          setExportJobId(null);
        } else if (status.status === 'expired') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          showToast({
            type: 'error',
            message: status.message || 'This export has expired. Please run the export again.',
            duration: 6000,
          });
          setExportJobId(null);
        } else if (status.status === 'failed') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          showToast({
            type: 'error',
            message: status.message || 'Export job failed.',
            duration: 5000,
          });
          setExportJobId(null);
        } else if (attempts >= maxAttempts) {
          // Not an error: large exports legitimately run longer than the poll
          // window, and the file is emailed when it completes.
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          showToast({
            type: 'success',
            message: "Still processing — we'll email the export to you when it's done.",
            duration: 6000,
          });
          setExportJobId(null);
        }
      } catch (err) {
        console.error('Error polling export status:', err);
        if (attempts >= maxAttempts) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setExportJobId(null);
        }
      }
    }, 5000); // Poll every 5 seconds
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleOrderAction = async (action, orderId, additionalData = {}) => {
    const normalizedAction = action === 'cancel_order' ? 'cancel' : action;

    if (!canConfirmOrders && (normalizedAction === 'confirm_order' || normalizedAction === 'confirmed')) {
      showToast({ type: 'error', message: 'You do not have permission to confirm orders.' });
      return;
    }

    if (!canCancelOrders && normalizedAction === 'cancel') {
      showToast({ type: 'error', message: 'You do not have permission to cancel orders.' });
      return;
    }

    // Only admins may move an order back to "New Order" status from any bucket.
    if (!isAdmin && normalizedAction === 'new_order') {
      showToast({ type: 'error', message: 'You do not have permission to move orders back to New Order.' });
      return;
    }

    // Only admins may place an order in the Held bucket.
    if (!isAdmin && normalizedAction === 'hold') {
      showToast({ type: 'error', message: 'You do not have permission to move orders to Held status.' });
      return;
    }

    // Handle update_order action: patch local state immediately without API call
    if (normalizedAction === 'update_order') {
      const { patch } = additionalData || {};
      if (patch && orderId) {
        setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...patch } : o));
      }
      return;
    }

    // Handle refresh_orders action (after label generation or tracking link update)
    if (normalizedAction === 'refresh_orders') {
      try {
        if (additionalData.source === 'tracking_link_update') {
          // Patch only the affected order's tracking link — no full re-fetch
          setOrders(prev => prev.map(o =>
            o.id === orderId ? { ...o, trackingLink: additionalData.trackingLink } : o
          ));
        } else if (additionalData.source === 'prescription_update') {
          // Patch only the affected order's prescription — no full re-fetch
          setOrders(prev => prev.map(o =>
            o.id === orderId ? { ...o, prescription: additionalData.prescription } : o
          ));
        } else {
          await refreshOrdersWithPagination();
        }
      } catch (error) {
        console.error('Error refreshing orders:', error);
      }
      return;
    }

    // Handle confirm_order action
    if (normalizedAction === 'confirm_order') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'confirmed', 'Order confirmed by user');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination();
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to confirm order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error confirming order:', error);
        alert(`Error confirming order: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle ready_to_ship action
    if (normalizedAction === 'ready_to_ship') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'ready_to_ship', 'Order marked ready to ship');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('confirmed');
          // Refresh counts after order status change
          await fetchCounts();
        } else if (response?.error_code === 'shipment_required') {
          // Stale list data let the request through; the backend caught it.
          showToast({ type: 'error', message: response.error || 'Label not generated for this order. Generate label or add manual shipping details before marking Ready to Ship.' });
        } else {
          showToast({ type: 'error', message: response?.error || response?.message || 'Failed to update order' });
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        showToast({ type: 'error', message: error.message || 'Error updating order status' });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle hold action
    if (normalizedAction === 'hold') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'held', 'Order held by user');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination();
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to hold order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error holding order:', error);
        alert(`Error holding order: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle cancel action
    if (normalizedAction === 'cancel') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'cancelled', 'Order cancelled by user');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination();
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to cancel order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error cancelling order:', error);
        alert(`Error cancelling order: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle new_order action (move back to new orders)
    if (normalizedAction === 'new_order') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'new', 'Order moved back to new orders');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination();
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to move order to new orders: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error moving order to new orders:', error);
        alert(`Error moving order to new orders: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle confirmed action (for all tab)
    if (normalizedAction === 'confirmed') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'confirmed', 'Order marked as confirmed');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('all');
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to update order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        alert(`Error updating order status: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle in_transit action (for all tab)
    if (normalizedAction === 'in_transit') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'in_transit', 'Order marked as in transit');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('all');
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to update order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        alert(`Error updating order status: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle delivered action (for all tab)
    if (normalizedAction === 'delivered') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'delivered', 'Order marked as delivered');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('all');
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to update order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        alert(`Error updating order status: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle exceptions action (for exceptions tab)
    if (normalizedAction === 'exceptions') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'exceptions', 'Order marked as exceptions');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('all');
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to update order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        alert(`Error updating order status: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle rto action (for in_transit and exceptions tabs)
    if (normalizedAction === 'rto') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'rto', 'Order marked as RTO');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('all');
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to update order: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating order status:', error);
        alert(`Error updating order status: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle retry action (for exceptions tab)
    if (normalizedAction === 'retry') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'in_transit', 'Order retry delivery initiated');

        if (response.success) {
          // Refresh the orders list
          await refreshOrdersWithPagination('all');
          // Refresh counts after order status change
          await fetchCounts();
        } else {
          alert(`Failed to retry delivery: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error retrying delivery:', error);
        alert(`Error retrying delivery: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle lost action
    if (normalizedAction === 'lost') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'lost', 'Order marked as lost');

        if (response.success) {
          await refreshOrdersWithPagination();
          await fetchCounts();
        } else {
          showToast({ type: 'error', message: `Failed to mark order as lost: ${response.message || 'Unknown error'}` });
        }
      } catch (error) {
        console.error('Error marking order as lost:', error);
        showToast({ type: 'error', message: `Error marking order as lost: ${error.message}` });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle damaged action
    if (normalizedAction === 'damaged') {
      try {
        setLoading(true);
        const response = await changeOrderStatus(orderId, 'damaged', 'Order marked as damaged');

        if (response.success) {
          await refreshOrdersWithPagination();
          await fetchCounts();
        } else {
          showToast({ type: 'error', message: `Failed to mark order as damaged: ${response.message || 'Unknown error'}` });
        }
      } catch (error) {
        console.error('Error marking order as damaged:', error);
        showToast({ type: 'error', message: `Error marking order as damaged: ${error.message}` });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle download_manifest action
    if (normalizedAction === 'download_manifest') {
      const order = orders.find(o => o.id === orderId);
      if (order && order.shipment?.manifest_url) {
        window.open(order.shipment.manifest_url, '_blank');
      } else {
        alert('Manifest URL not available for this order');
      }
      return;
    }

    // Handle download_label action
    if (normalizedAction === 'download_label') {
      const order = orders.find(o => o.id === orderId);
      const labelUrl = order?.shipment?.label_url;
      if (labelUrl) {
        window.open(labelUrl, '_blank');
      } else {
        alert('Label URL not available for this order');
      }
      return;
    }

    // Handle download_invoice action
    if (normalizedAction === 'download_invoice') {
      try {
        const data = await getOrderInvoice(orderId);
        if (data && data.invoice_url) {
          window.open(data.invoice_url, '_blank');
        } else {
          alert(data?.error || 'Invoice URL not available for this order');
        }
      } catch (error) {
        console.error('Error downloading invoice:', error);
        alert(error.message || 'Failed to download invoice');
      }
      return;
    }

    // Handle email_vendor action
    if (normalizedAction === 'email_vendor') {
      try {
        const response = await emailOrderToVendor(orderId);
        if (response.success && (response.total_failed ?? 0) === 0) {
          showToast({ type: 'success', message: 'Order email sent to vendor successfully' });
        } else {
          const reason =
            response.errors?.[0]?.reason ||
            response.error ||
            'Failed to send email to vendor';
          showToast({
            type: 'error',
            message: reason,
          });
        }
      } catch (error) {
        console.error('Error emailing order to vendor:', error);
        showToast({ type: 'error', message: error.message || 'Failed to send email to vendor' });
      }
      return;
    }

    // Handle email_invoice action
    if (normalizedAction === 'email_invoice') {
      // Guard: check for an active invoice before calling the API
      const order = orders.find(o => o.id === orderId);
      if (order && !(order.invoice && order.invoice.has_active)) {
        showToast({
          type: 'warning',
          message: '⚠️ No active invoice found for this order. Generate an invoice first before emailing it to the customer.',
          duration: 5000,
        });
        return;
      }

      try {
        const response = await emailInvoiceToCustomer(orderId);
        if (response.success && (response.total_failed ?? 0) === 0) {
          showToast({ type: 'success', message: 'Invoice email sent to customer successfully' });
        } else {
          const reason =
            response.errors?.[0]?.reason ||
            response.error ||
            'Failed to send invoice to customer';
          showToast({
            type: 'error',
            message: reason,
          });
        }
      } catch (error) {
        console.error('Error emailing invoice to customer:', error);
        showToast({ type: 'error', message: error.message || 'Failed to send invoice to customer' });
      }
      return;
    }

    // Handle email_address_verification action
    if (normalizedAction === 'email_address_verification') {
      try {
        const response = await sendAddressVerificationEmail(orderId);
        if (response.success && (response.total_failed ?? 0) === 0) {
          showToast({ type: 'success', message: 'Address verification email sent to customer' });
        } else {
          const reason =
            response.errors?.[0]?.reason ||
            response.error ||
            'Failed to send address verification email';
          showToast({ type: 'error', message: reason });
        }
      } catch (error) {
        console.error('Error sending address verification email:', error);
        showToast({ type: 'error', message: error.message || 'Failed to send address verification email' });
      }
      return;
    }

    // Handle other actions - no alerts, just console log
    console.log(`Action ${action} on order ${orderId}`);
  };

  const getTabCounts = () => {
    // If we have API counts, use them; otherwise calculate from local orders
    if (orderCounts) {
      return {
        new_orders: orderCounts.new?.count || 0,
        confirmed: orderCounts.confirmed?.count || 0,
        ready_to_ship: orderCounts.ready_to_ship?.count || 0,
        in_transit: orderCounts.in_transit?.count || 0,
        exceptions: orderCounts.exceptions?.count || 0,
        delivered: orderCounts.delivered?.count || 0,
        rto: orderCounts.rto?.count || 0,
        held: orderCounts.held?.count || 0,
        cancelled: orderCounts.cancelled?.count || 0,
        lost: orderCounts.lost?.count || 0,
        damaged: orderCounts.damaged?.count || 0,
        all: orderCounts.all_orders?.count || 0
      };
    }

    // Fallback: calculate from local orders
    const counts = {
      new_orders: 0,
      confirmed: 0,
      ready_to_ship: 0,
      in_transit: 0,
      exceptions: 0,
      delivered: 0,
      rto: 0,
      held: 0,
      cancelled: 0,
      lost: 0,
      damaged: 0,
      all: orders.length
    };

    orders.forEach(order => {
      const status = order.orderStatus.toLowerCase().replace(/ /g, '_');
      if (counts.hasOwnProperty(status)) {
        counts[status]++;
      }
    });

    return counts;
  };

  const tabCounts = getTabCounts();

  const tabs = [
    { id: 'new_orders', label: 'New Orders', count: tabCounts.new_orders },
    { id: 'confirmed', label: 'Confirmed', count: tabCounts.confirmed },
    { id: 'ready_to_ship', label: 'Ready to Ship', count: tabCounts.ready_to_ship },
    { id: 'in_transit', label: 'In Transit', count: tabCounts.in_transit },
    { id: 'exceptions', label: 'Exceptions', count: tabCounts.exceptions },
    { id: 'delivered', label: 'Delivered', count: tabCounts.delivered },
    { id: 'rto', label: 'RTO', count: tabCounts.rto },
    { id: 'held', label: 'Held', count: tabCounts.held },
    { id: 'cancelled', label: 'Cancelled', count: tabCounts.cancelled },
    { id: 'lost', label: 'Lost', count: tabCounts.lost },
    { id: 'damaged', label: 'Damaged', count: tabCounts.damaged },
    { id: 'all', label: 'All Orders', count: tabCounts.all }
  ];

  // Vendors do not see the New Orders tab (admin confirmation queue) or the Held
  // bucket (admin-only quarantine); held orders remain visible under "All Orders".
  const VENDOR_HIDDEN_TABS = ['new_orders', 'held'];
  const visibleTabs = userRole === 'vendor' ? tabs.filter(tab => !VENDOR_HIDDEN_TABS.includes(tab.id)) : tabs;

  // Rows stay on screen for as long as the outage lasts, with the banner
  // stating exactly when they were true. Much of this screen is read-only work
  // — checking an address, reading a customer's details — and taking that away
  // to show a message helps nobody. What makes it safe is that the controls are
  // locked, so nothing can be acted on against figures that stopped moving.
  const isStale = Boolean(error) && paginatedOrders.length > 0;

  // Deliberately no early return for `loading` or `error`. The page frame — tabs, filters,
  // search — is fine and still usable; only the rows are missing. Replacing the
  // whole screen used to strand the user with a reload button, when what they
  // wanted was to change tab or adjust a filter and try again.

  return (
    <div className="manageOrdersContainer">
      {/* Shipping Escalation Loading Overlay */}
      <EscalationLoadingOverlay />
      
      {/* Forward Order and Exception Order are peers: two views of the same
          screen with different row entities (an order versus a single label).
          They are the page's own heading — a separate title above them said
          nothing the tabs did not already say. The generate button shares the
          row and stays on both tabs, since raising a return or a replacement
          usually starts from looking at the order it belongs to. */}
      <div className="sectionBar">
        {isAdmin ? (
          <div className="sectionTabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={section === 'orders'}
              className={`sectionTab ${section === 'orders' ? 'isActive' : ''}`}
              onClick={() => setSection('orders')}
            >
              Forward Order
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={section === 'exceptions'}
              className={`sectionTab ${section === 'exceptions' ? 'isActive' : ''}`}
              onClick={() => setSection('exceptions')}
            >
              Exception Order
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={section === 'manual'}
              className={`sectionTab ${section === 'manual' ? 'isActive' : ''}`}
              onClick={() => setSection('manual')}
            >
              Manual Order
            </button>
          </div>
        ) : (
          <h1 className="title">Forward Order</h1>
        )}
        <div className="sectionBarRight">
          {isAdmin && (
            <button
              type="button"
              className="generateExceptionLabelBtn"
              onClick={() => navigate('/suspire-terminal/labels')}
              title="Create a return or replacement label"
            >
              + Generate Exception Label
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="newManualOrderBtn"
              onClick={() => navigate('/suspire-terminal/manual-orders/new')}
              title="Capture an order placed outside Shopify"
            >
              + New Manual Order
            </button>
          )}
        </div>
      </div>

      {section === 'manual' ? (
        <ManualOrdersTable showToast={showToast} />
      ) : section === 'exceptions' ? (
        <ExceptionLabelsTable showToast={showToast} />
      ) : (
      <>
      <div className="orderTabs">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`tabButton ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setCurrentPage(1);
              setSelectAllMode(false);
              setSelectedOrders([]);
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <div className="toolbar">
        <div className="toolbarLeft">
          <div className="searchInputWrapper">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Order Number"
              value={filters.globalSearch}
              onChange={(e) => handleFilterChange({ globalSearch: e.target.value })}
              className="searchInput"
            />
            {filters.globalSearch && (
              <button
                type="button"
                className="searchClearButton"
                onClick={() => {
                  handleFilterChange({ globalSearch: '' });
                  searchInputRef.current?.focus();
                }}
                title="Clear search"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <button
            className={`filterButton ${activeAdvancedFilterCount > 0 ? 'hasActiveFilters' : ''}`}
            onClick={() => setShowAdvancedFiltersModal(true)}
            title="Filters"
          >
            <span className="filterIcon">⚙️</span>
            {activeAdvancedFilterCount > 0 && (
              <span className="filterBadge">{activeAdvancedFilterCount}</span>
            )}
          </button>
        </div>
        <div className="toolbarRight">
        {isAdmin && (activeTab === 'ready_to_ship' || activeTab === 'in_transit') && (
            <button
              className="bulkActionsButton"
              onClick={handleEscalation}
              disabled={isEscalating}
              title="Shipping Escalation"
            >
              Escalate Orders
            </button>
          )}
          {(isAdmin || isVendor) && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                className="bulkActionsButton"
                onClick={() => setShowBulkActionsMenu(prev => !prev)}
                disabled={selectedOrders.length === 0 && !selectAllMode}
              >
                Bulk Actions ▾
              </button>
              {showBulkActionsMenu && (
                <>
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 999
                    }}
                    onClick={() => setShowBulkActionsMenu(false)}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      background: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                      minWidth: '210px',
                      zIndex: 1000,
                      overflow: 'hidden',
                      padding: '8px 0',
                    }}
                  >
                    <div className="bulkActionHeading">
                      Generate
                    </div>
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        setBulkInvoiceMode('generate');
                        setShowBulkInvoiceModal(true);
                      }}
                    >
                      Invoice
                    </button>
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleBulkGenerateManifestLabel();
                      }}
                      disabled={isBulkManifestLabelGenerating}
                    >
                      {isBulkManifestLabelGenerating ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div className="spinner small" style={{ marginRight: '8px', borderLeftColor: '#007bff' }}></div>
                          Generating...
                        </div>
                      ) : (
                        'Manifest & Label'
                      )}
                    </button>

                    <div className="bulkActionHeading">
                      Cancel
                    </div>
                    {isAdmin && (
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        handleCancelLabels();
                      }}
                    >
                      Shipping Label
                    </button>
                    )}
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        setBulkInvoiceMode('cancel');
                        setShowBulkInvoiceModal(true);
                      }}
                    >
                      Invoice
                    </button>

                    <div className="bulkActionHeading">
                      Update/Assign
                    </div>
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        setShowBulkDimensionsModal(true);
                      }}
                    >
                      Package Dimension
                    </button>
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        setShowBulkPickupModal(true);
                      }}
                    >
                      Pickup Address
                    </button>
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        setShowBulkWeightModal(true);
                      }}
                    >
                      Package Weight
                    </button>

                    {isAdmin && (
                      <>
                        <div className="bulkActionHeading">
                          Email
                        </div>
                        <button
                          className="bulkActionItem"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBulkActionsMenu(false);
                            setBulkEmailMode('vendor');
                            setShowBulkEmailModal(true);
                          }}
                        >
                          Orders to Vendor
                        </button>
                        <button
                          className="bulkActionItem"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBulkActionsMenu(false);
                            setBulkEmailMode('customer');
                            setShowBulkEmailModal(true);
                          }}
                        >
                          Invoices to Customers
                        </button>
                      </>
                    )}

                    <div className="bulkActionHeading">
                      Status
                    </div>
                    <button
                      className="bulkActionItem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowBulkActionsMenu(false);
                        setShowBulkStatusModal(true);
                      }}
                    >
                      Change Status
                    </button>

                  </div>
                </>
              )}
            </div>
          )}
          <button
            className="downloadButton"
            onClick={() => setShowExportModal(true)}
            disabled={selectedOrders.length === 0 && !selectAllMode}
          >
            ⬇️
          </button>
          {isAdmin && (
            <button
              className="bulkActionsButton"
              onClick={handleExportAdminOrderLogs}
              disabled={isExportingAdminLogs}
              title="Export Order Audit Logs"
            >
              {isExportingAdminLogs ? 'Exporting...' : 'Export Audit Logs'}
            </button>
          )}
          {isAdmin && (
            <BulkPaymentStatusMenu
              selectedOrders={selectedOrders}
              selectAllMode={selectAllMode}
              totalCount={totalCount}
              onUpdateStatus={handleBulkPaymentUpdate}
              isLoading={isPageLoading}
            />
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {activeAdvancedFilterCount > 0 && (
        <div className="activeAdvancedFilters">
          <span className="activeFiltersLabel">Active Filters:</span>
          <div className="filterChips">
            {advancedFilters.orderNumber && (
              <span className="filterChip">
                Order: {advancedFilters.orderNumber}
                <button onClick={() => handleRemoveAdvancedFilter('orderNumber')}>×</button>
              </span>
            )}
            {advancedFilters.customer && (
              <span className="filterChip">
                Customer: {advancedFilters.customer}
                <button onClick={() => handleRemoveAdvancedFilter('customer')}>×</button>
              </span>
            )}
            {advancedFilters.awb && (
              <span className="filterChip">
                AWB: {advancedFilters.awb}
                <button onClick={() => handleRemoveAdvancedFilter('awb')}>×</button>
              </span>
            )}
            {advancedFilters.carrierId && (
              <span className="filterChip">
                Carrier selected
                <button onClick={() => handleRemoveAdvancedFilter('carrierId')}>×</button>
              </span>
            )}
            {advancedFilters.labelGenerated && advancedFilters.labelGenerated !== 'all' && (
              <span className="filterChip">
                Label: {advancedFilters.labelGenerated === 'true' ? 'Yes' : 'No'}
                <button onClick={() => handleRemoveAdvancedFilter('labelGenerated')}>×</button>
              </span>
            )}
            {advancedFilters.invoiceGenerated && advancedFilters.invoiceGenerated !== 'all' && (
              <span className="filterChip">
                Invoice: {advancedFilters.invoiceGenerated === 'true' ? 'Yes' : 'No'}
                <button onClick={() => handleRemoveAdvancedFilter('invoiceGenerated')}>×</button>
              </span>
            )}
            {advancedFilters.shipmentStatus && advancedFilters.shipmentStatus !== 'all' && (
              <span className="filterChip">
                Shipment: {advancedFilters.shipmentStatus}
                <button onClick={() => handleRemoveAdvancedFilter('shipmentStatus')}>×</button>
              </span>
            )}
            {advancedFilters.paymentStatus && advancedFilters.paymentStatus !== 'all' && (
              <span className="filterChip">
                Payment: {advancedFilters.paymentStatus}
                <button onClick={() => handleRemoveAdvancedFilter('paymentStatus')}>×</button>
              </span>
            )}
            {advancedFilters.dateFrom && (
              <span className="filterChip">
                From: {advancedFilters.dateFrom}
                <button onClick={() => handleRemoveAdvancedFilter('dateFrom')}>×</button>
              </span>
            )}
            {advancedFilters.dateTo && (
              <span className="filterChip">
                To: {advancedFilters.dateTo}
                <button onClick={() => handleRemoveAdvancedFilter('dateTo')}>×</button>
              </span>
            )}
            {advancedFilters.tags && advancedFilters.tags.length > 0 && (
              <span className="filterChip">
                Tags: {advancedFilters.tags.join(', ')}
                <button onClick={() => handleRemoveAdvancedFilter('tags')}>×</button>
              </span>
            )}
            {advancedFilters.vendorIds && advancedFilters.vendorIds.length > 0 && (
              <span className="filterChip">
                {advancedFilters.vendorIds.length} vendor(s)
                <button onClick={() => handleRemoveAdvancedFilter('vendorIds')}>×</button>
              </span>
            )}
          </div>
          <button
            className="clearAllFiltersBtn"
            onClick={handleClearAllFilters}
          >
            Clear All
          </button>
        </div>
      )}

      <div className="resultsSummary">
        <p>
          Showing {startIndex + 1}-{Math.min(endIndex, startIndex + filteredOrders.length)} of {totalCount} orders
        </p>
      </div>

      {(selectedOrders.length > 0 || selectAllMode) && (
        <div className="selectionBanner">
          <div className="selectionBannerContent">
            <span className="selectionCount">
              {selectAllMode ? `All ${totalCount} Orders Selected.` : `(${selectedOrders.length}) Orders Selected.`}
            </span>
            <button
              className="selectionBannerButton"
              onClick={handleUnselectAll}
            >
              Unselect Orders
            </button>
            <span className="selectionBannerDivider">/</span>
            <button
              className="selectionBannerButton"
              onClick={handleSelectAllGlobal}
            >
              Select All {totalCount} Orders
            </button>
          </div>
        </div>
      )}

      {/*
        When a load fails with nothing to fall back on, only this region
        changes: the tabs, filters and search above stay usable, so switching
        tab or narrowing a filter is still a way forward. If rows are already on
        screen we keep them and show the stale notice instead — losing the list
        someone is working through is worse than showing figures a minute old,
        and any action they attempt hits the same unavailable server anyway.
      */}
      {isStale && (
        <StaleDataBanner since={lastLoadedAt} onRetry={retryFetchOrders} actionsPaused />
      )}

      {loading && paginatedOrders.length === 0 ? (
        // The original spinner, kept deliberately: loading states are the same
        // everywhere in the app and a table-shaped placeholder here would be the
        // odd one out. It sits inside the rows region rather than replacing the
        // page, so the tabs and filters stay put.
        <div className="loadingContainer">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      ) : error && paginatedOrders.length === 0 ? (
        /*
          Only when there is nothing to show. With rows on screen we keep them:
          an empty message helps nobody who was mid-task, and the banner above
          says exactly how old they are.
        */
        <DataFallback
          variant="table"
          error={error}
          onRetry={retryFetchOrders}
        />
      ) : (
      <OrderListTable
        orders={paginatedOrders}
        sortField={sortState.field}
        sortDirection={sortState.direction}
        onSort={handleSort}
        onOrderAction={handleOrderAction}
        onOrderSelect={handleOrderSelect}
        onSelectAll={handleSelectAll}
        selectedOrders={selectedOrders}
        selectAllMode={selectAllMode}
        editableDimensions={editableDimensions}
        selectedProzoChannels={selectedProzoChannels}
        onDimensionChange={handleDimensionChange}
        onProzoChannelChange={handleProzoChannelChange}
        // Also read-only while we cannot reach the server. The rows are still
        // worth reading, but editing a weight or firing a manifest against a
        // backend that is not answering would fail — and with the reassuring
        // copy we now show on writes, it could easily read as "queued" rather
        // than "not saved".
        isReadOnly={isReadOnlyAuditor || Boolean(error)}
        userRole={userRole}
        isLoading={isPageLoading}
        userId={userId}
        activeTab={activeTab}
        canConfirmOrders={canConfirmOrders}
        canCancelOrders={canCancelOrders}
        canEditShipToAddresses={canEditShipToAddresses}
      />
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      </>
      )}

      {/* Export Modal */}
      <ExportOrdersModal
        isOpen={showExportModal}
        onClose={() => {
          setShowExportModal(false);
          setExportError('');
        }}
        onConfirm={handleExportConfirm}
        isSubmitting={isExporting}
        error={exportError}
        selectedCount={selectedOrders.length}
        filteredCount={filteredOrders.length}
      />

      {/* Download link for an export that finished while the page was open */}
      {exportReady && (
        <div
          style={{
            position: 'fixed', right: 20, bottom: 20, zIndex: 9999,
            background: '#fff', border: '1px solid #99f6e4', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 12, maxWidth: 380,
          }}
          role="status"
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#004442' }}>Export ready</div>
            <div style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-all' }}>{exportReady.filename}</div>
          </div>
          <a
            href={exportReady.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setExportReady(null)}
            style={{ padding: '7px 14px', background: '#00bfae', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
          >
            Download
          </a>
          <button
            onClick={() => setExportReady(null)}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      )}

      {/* Export Banner Modal — shown after a successful export */}
      <ExportBannerModal
        isOpen={showExportBannerModal}
        banners={exportBanners}
        onClose={() => setShowExportBannerModal(false)}
      />

      {/* Bulk Status Update Modal */}
      <BulkStatusModal
        isOpen={showBulkStatusModal}
        onClose={() => setShowBulkStatusModal(false)}
        selectedOrders={selectedOrders}
        activeTab={activeTab}
        canConfirmOrders={canConfirmOrders}
        canCancelOrders={canCancelOrders}
        onConfirm={handleBulkStatusUpdateConfirm}
        isSubmitting={isBulkUpdating}
        orders={orders}
        userRole={userRole}
      />

      <BulkPickupAddressModal
        isOpen={showBulkPickupModal}
        onClose={() => setShowBulkPickupModal(false)}
        selectedOrders={selectedOrders}
        orders={orders}
        onConfirm={handleBulkPickupConfirm}
        isSubmitting={isBulkPickupUpdating}
      />

      <BulkInvoiceModal
        isOpen={showBulkInvoiceModal}
        onClose={() => setShowBulkInvoiceModal(false)}
        selectedOrders={selectedOrders}
        orders={orders}
        mode={bulkInvoiceMode}
        onConfirm={handleBulkInvoiceConfirm}
        isSubmitting={isBulkInvoiceGenerating}
      />

      <BulkEmailModal
        isOpen={showBulkEmailModal}
        onClose={() => setShowBulkEmailModal(false)}
        selectedOrders={selectedOrders}
        orders={orders}
        mode={bulkEmailMode}
        onConfirm={handleBulkEmail}
        isSubmitting={isBulkEmailSending}
        selectAllMode={selectAllMode}
        totalSelectAllCount={serverPagination?.total_count || selectedOrders.length}
      />

      <BulkWeightModal
        isOpen={showBulkWeightModal}
        onClose={() => setShowBulkWeightModal(false)}
        selectedOrdersCount={selectAllMode ? (serverPagination?.total_count || selectedOrders.length) : selectedOrders.length}
        onConfirm={handleBulkWeightConfirm}
        isSubmitting={false}
      />

      <BulkDimensionsModal
        isOpen={showBulkDimensionsModal}
        onClose={() => setShowBulkDimensionsModal(false)}
        selectedOrdersCount={selectAllMode ? (serverPagination?.total_count || selectedOrders.length) : selectedOrders.length}
        onConfirm={handleBulkDimensionsConfirm}
        isSubmitting={isBulkDimensionsUpdating}
      />

      {prescriptionModalOrderId && orders.find(o => o.id === prescriptionModalOrderId) && (
        <PrescriptionUploadModal
          order={orders.find(o => o.id === prescriptionModalOrderId)}
          mode="required"
          canUpload={!isReadOnlyAuditor}
          onClose={() => setPrescriptionModalOrderId(null)}
          onSaved={(prescription) => {
            const savedId = prescriptionModalOrderId;
            setOrders(prev => prev.map(o => (o.id === savedId ? { ...o, prescription } : o)));
          }}
        />
      )}

      <MultiBrandConflictModal
        isOpen={showMultiBrandModal}
        onClose={() => setShowMultiBrandModal(false)}
        brands={multiBrandConflictData}
      />

      <BulkManifestValidationModal
        isOpen={showValidationErrorsModal}
        onClose={() => setShowValidationErrorsModal(false)}
        validationErrors={validationErrorsData}
        processed={processedOrdersCount}
        onProceed={handleProceedWithValidOrders}
      />

      {/* Filters Modal */}
      {showAdvancedFiltersModal && (
        <div className="advancedFiltersOverlay" onClick={() => setShowAdvancedFiltersModal(false)}>
          <div className="advancedFiltersModal" onClick={e => e.stopPropagation()}>
            <div className="modalHeader">
              <h2>Filters</h2>
              <button className="closeButton" onClick={() => setShowAdvancedFiltersModal(false)}>×</button>
            </div>

            {isLoadingFilterOptions ? (
              <div className="loadingState">Loading filter options...</div>
            ) : (
              <div className="modalContent">
                {/* Date Filters Section */}
                <div className="filterSection">
                  <h3 className="sectionTitle">📅 Date Filters</h3>
                  <div className="filterGrid">
                    <div className="filterGroup">
                      <label>Order Date From</label>
                      <input
                        type="date"
                        value={localFilters.dateFrom || ''}
                        onChange={e => handleFilterInputChange('dateFrom', e.target.value)}
                        className="filterInput"
                      />
                    </div>
                    <div className="filterGroup">
                      <label>Order Date To</label>
                      <input
                        type="date"
                        value={localFilters.dateTo || ''}
                        onChange={e => handleFilterInputChange('dateTo', e.target.value)}
                        className="filterInput"
                      />
                    </div>
                    <div className="filterGroup">
                      <label>Delivered From</label>
                      <input
                        type="date"
                        value={localFilters.deliveredFrom || ''}
                        onChange={e => handleFilterInputChange('deliveredFrom', e.target.value)}
                        className="filterInput"
                      />
                    </div>
                    <div className="filterGroup">
                      <label>Delivered To</label>
                      <input
                        type="date"
                        value={localFilters.deliveredTo || ''}
                        onChange={e => handleFilterInputChange('deliveredTo', e.target.value)}
                        className="filterInput"
                      />
                    </div>
                  </div>
                </div>

                {/* Order Identification Section */}
                <div className="filterSection">
                  <h3 className="sectionTitle">🔍 Order Identification</h3>
                  <div className="filterGrid">
                    <div className="filterGroup">
                      <label>Channel</label>
                      <select
                        value={localFilters.channel || 'all'}
                        onChange={e => handleFilterInputChange('channel', e.target.value)}
                        className="filterSelect"
                      >
                        <option value="all">All Channels</option>
                        <option value="shopify">Shopify</option>
                        <option value="website">Website</option>
                        <option value="marketplace">Marketplace</option>
                      </select>
                    </div>
                    <div className="filterGroup">
                      <label>AWB Number</label>
                      <input
                        type="text"
                        placeholder="Search by AWB..."
                        value={localFilters.awb || ''}
                        onChange={e => handleFilterInputChange('awb', e.target.value)}
                        className="filterInput"
                      />
                    </div>
                    <div className="filterGroup">
                      <label>Customer</label>
                      <div className="searchableDropdown" ref={customerDropdownRef}>
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={customerSearchTerm}
                          onChange={handleCustomerSearchChange}
                          onFocus={() => {
                            setShowCustomerDropdown(true);
                            if (customerSearchTerm.trim().length >= 2) {
                              performCustomerSearch(customerSearchTerm);
                            }
                          }}
                          className="filterInput"
                          autoComplete="off"
                        />
                        {showCustomerDropdown && (
                          <div className="dropdownMenu">
                            <div className="dropdownItem" onClick={() => handleCustomerSelect(null)}>
                              All Customers
                            </div>
                            {customerSuggestions.length > 0 ? (
                              customerSuggestions.map((c) => (
                                <div
                                  key={c.id}
                                  className="dropdownItem"
                                  onClick={() => handleCustomerSelect(c)}
                                >
                                  {highlightMatch(c.display, customerSearchTerm)}
                                  {(c.email || c.phone) && (
                                    <span style={{ marginLeft: 6, color: '#666', fontSize: 12 }}>
                                      {c.email || c.phone}
                                    </span>
                                  )}
                                </div>
                              ))
                            ) : customerSearchTerm.trim().length >= 2 ? (
                              <div className="dropdownItem dropdownItemDisabled">No customers found</div>
                            ) : (
                              <div className="dropdownItem dropdownItemDisabled">Type at least 2 characters...</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Section */}
                <div className="filterSection">
                  <h3 className="sectionTitle">📦 Shipping</h3>
                  <div className="filterGrid">
                    <div className="filterGroup">
                      <label>Carrier/Shipper</label>
                      <select
                        value={localFilters.carrierId || ''}
                        onChange={e => handleFilterInputChange('carrierId', e.target.value)}
                        className="filterSelect"
                      >
                        <option value="">All Carriers</option>
                        {filterOptions.carriers.map(carrier => (
                          <option key={carrier.id} value={carrier.id}>
                            {carrier.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {activeTab === 'all' && (
                      <div className="filterGroup">
                        <label>Shipment Status</label>
                        <select
                          value={localFilters.shipmentStatus || 'all'}
                          onChange={e => handleFilterInputChange('shipmentStatus', e.target.value)}
                          className="filterSelect"
                        >
                          <option value="all">All Statuses</option>
                          {filterOptions.trackingStatuses.map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="filterGroup">
                      <label>Label Generated</label>
                      <select
                        value={localFilters.labelGenerated || 'all'}
                        onChange={e => handleFilterInputChange('labelGenerated', e.target.value)}
                        className="filterSelect"
                      >
                        <option value="all">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                    <div className="filterGroup">
                      <label>Invoice Generated</label>
                      <select
                        value={localFilters.invoiceGenerated || 'all'}
                        onChange={e => handleFilterInputChange('invoiceGenerated', e.target.value)}
                        className="filterSelect"
                      >
                        <option value="all">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Status Section */}
                <div className="filterSection">
                  <h3 className="sectionTitle">📋 Status</h3>
                  <div className="filterGrid">
                    <div className="filterGroup">
                      <label>Payment Status</label>
                      <select
                        value={localFilters.paymentStatus || 'all'}
                        onChange={e => handleFilterInputChange('paymentStatus', e.target.value)}
                        className="filterSelect"
                      >
                        <option value="all">All</option>
                        {filterOptions.financialStatuses.map(status => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                          </option>
                        ))}
                      </select>
                    </div>
                    {activeTab === 'rto' && (
                      <div className="filterGroup">
                        <label>RTO Status</label>
                        <div className="multiSelectDropdown" ref={rtoSubStatusDropdownRef}>
                          <button
                            type="button"
                            className="multiSelectTrigger"
                            onClick={() => setShowRtoSubStatusDropdown(prev => !prev)}
                          >
                            <span>
                              {(localFilters.rtoSubStatuses || []).length > 0
                                ? `${localFilters.rtoSubStatuses.length} selected`
                                : 'All RTO Statuses'}
                            </span>
                            <span className="multiSelectCaret">▾</span>
                          </button>
                          {showRtoSubStatusDropdown && (
                            <div className="multiSelectMenu">
                              {filterOptions.rtoSubStatuses.map(sub => {
                                const selected = (localFilters.rtoSubStatuses || []).includes(sub.value);
                                return (
                                  <label key={sub.value} className="multiSelectOption">
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={e => handleMultiSelectChange('rtoSubStatuses', sub.value, e.target.checked)}
                                    />
                                    <span>{sub.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vendors Section (Admin Only) */}
                {isAdmin && (
                  <div className="filterSection">
                    <h3 className="sectionTitle">🏢 Vendors</h3>

                    {/* Selected Vendors */}
                    {selectedVendors.length > 0 && (
                      <div className="selectedVendors">
                        {selectedVendors.map(vendor => (
                          <span key={vendor.id} className="vendorChip">
                            {vendor.brand_name || vendor.company_name}
                            <button
                              onClick={() => handleRemoveVendor(vendor.id)}
                              className="removeVendorBtn"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Vendor Search */}
                    <div className="searchableDropdown" ref={vendorDropdownRef}>
                      <input
                        type="text"
                        placeholder="Search vendors..."
                        value={vendorSearchTerm}
                        onChange={handleVendorSearchChange}
                        onFocus={() => {
                          setShowVendorDropdown(true);
                          if (vendorSearchTerm.trim().length >= 2) {
                            performVendorSearch(vendorSearchTerm);
                          }
                        }}
                        className="filterInput"
                        autoComplete="off"
                      />
                      {showVendorDropdown && (
                        <div className="dropdownMenu">
                          {vendorLoading ? (
                            <div className="dropdownItem dropdownItemDisabled">
                              Searching...
                            </div>
                          ) : vendorSuggestions.length > 0 ? (
                            vendorSuggestions.map((vendor) => {
                              const isSelected = selectedVendors.some(v => v.id === vendor.id);
                              return (
                                <div
                                  key={vendor.id}
                                  className={`dropdownItem ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleVendorSelect(vendor)}
                                >
                                  {isSelected && <span className="checkmark">✓ </span>}
                                  {highlightMatch(vendor.brand_name || vendor.company_name, vendorSearchTerm)}
                                </div>
                              );
                            })
                          ) : vendorSearchTerm.trim().length >= 2 ? (
                            <div className="dropdownItem dropdownItemDisabled">
                              No vendors found
                            </div>
                          ) : (
                            <div className="dropdownItem dropdownItemDisabled">
                              Type at least 2 characters...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tags Section */}
                <div className="filterSection">
                  <h3 className="sectionTitle">🏷️ Tags</h3>

                  {/* Tag Search Dropdown */}
                  <div className="searchableDropdown" ref={tagDropdownRef}>
                    <input
                      type="text"
                      placeholder="Search tags..."
                      value={tagSearchTerm}
                      onChange={handleTagSearchChange}
                      onFocus={() => {
                        setShowTagDropdown(true);
                        if (tagSearchTerm.trim().length >= 1) {
                          performTagSearch(tagSearchTerm);
                        }
                      }}
                      className="filterInput"
                      autoComplete="off"
                    />
                    {showTagDropdown && (
                      <div className="dropdownMenu">
                        {tagSuggestions.length > 0 ? (
                          tagSuggestions.map((tag) => {
                            const isSelected = selectedTags.includes(tag);
                            return (
                              <div
                                key={tag}
                                className={`dropdownItem ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleTagSelect(tag)}
                              >
                                {isSelected && <span className="checkmark">✓ </span>}
                                {tag}
                              </div>
                            );
                          })
                        ) : tagSearchTerm.trim().length >= 1 ? (
                          <div className="dropdownItem dropdownItemDisabled">
                            No tags found
                          </div>
                        ) : (
                          <div className="dropdownItem dropdownItemDisabled">
                            Type to search tags
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Selected Tags Chips */}
                  {selectedTags.length > 0 && (
                    <div className="selectedTags" style={{ marginTop: '10px' }}>
                      {selectedTags.map(tag => (
                        <span key={tag} className="vendorChip">
                          {tag}
                          <button onClick={() => handleRemoveTagChip(tag)} className="removeVendorBtn">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="modalFooter">
              <div className="activeFilterCount">
                {getActiveFilterCount() > 0 && (
                  <span>{getActiveFilterCount()} filter{getActiveFilterCount() > 1 ? 's' : ''} active</span>
                )}
              </div>
              <div className="footerButtons">
                <button onClick={() => setShowAdvancedFiltersModal(false)} className="cancelButton">
                  Cancel
                </button>
                <button onClick={handleAdvancedFiltersApply} className="applyButton">
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Map API order response to component format
function mapApiOrderToComponent(apiOrder) {
  // Map status names
  const statusMap = {
    'new': 'New Orders',
    'confirmed': 'Confirmed',
    'ready_to_ship': 'Ready to Ship',
    'in_transit': 'In Transit',
    'exceptions': 'Exceptions',
    'delivered': 'Delivered',
    'rto': 'RTO',
    'held': 'Held',
    'cancelled': 'Cancelled',
    'lost': 'Lost',
    'damaged': 'Damaged'
  };

  const orderDate = new Date(apiOrder.created_at);

  // total_price is the brand-listed (pre-discount) amount; net payable
  // subtracts any Suspire discount to show what the customer actually pays,
  // matching Order Details and the backend's Order.net_payable_amount.
  const netPayablePrice = Math.max(
    0,
    parseFloat(apiOrder.total_price || 0) - parseFloat(apiOrder.suspire_discount_amount || 0)
  );

  // Extract product information from line items
  const lineItems = apiOrder.line_items || [];
  const firstItem = lineItems[0] || {};
  const productImages = [];
  const productMediaEntries = [];
  const seenVariants = new Set();

  // One image per ordered variant (so Pack of 2 and Pack of 4 of the same product each show),
  // keeping name/quantity in sync for the carousel
  lineItems.forEach((item) => {
    const variantKey = item.variant?.id || item.variant?.product?.id;
    if (variantKey && seenVariants.has(variantKey)) {
      return;
    }

    // Prefer the image assigned to the ordered variant; fall back to the product's first image
    const mediaArray = item.variant?.product?.media || [];
    const firstMediaWithUrl = mediaArray.find((media) => media && media.url);
    const imageUrl = item.variant?.image?.url || firstMediaWithUrl?.url || '';
    const productName = item.product_name || item.variant?.product?.title || 'No Product';
    // Units still to ship (cancelled-on-Shopify units excluded).
    const quantity = remainingQuantity(item);

    if (imageUrl) {
      productImages.push(imageUrl);
    }

    productMediaEntries.push({ image: imageUrl, name: productName, quantity });

    if (variantKey) {
      seenVariants.add(variantKey);
    }
  });

  // Fallback to at least one entry so UI has something to show
  if (productMediaEntries.length === 0) {
    productMediaEntries.push({
      image: productImages[0] || '',
      name: firstItem.product_name || 'No Product',
      quantity: firstItem.quantity ?? (apiOrder.line_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) ?? 0),
    });
  }

  return {
    id: apiOrder.id,
    orderNumber: apiOrder.order_number,
    orderDate: orderDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    orderTime: orderDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
    vendorId: apiOrder.vendor?.id || 'unknown',
    vendorName: apiOrder.vendor?.brand_name || apiOrder.vendor?.company_name || 'Unknown Vendor',
    vendorHideCustomerDetails: apiOrder.vendor?.hide_customer_details || false,
    integrationType: 'Standard', // Default since not in API
    productCount: apiOrder.line_items?.length || 0,
    itemCount: apiOrder.line_items?.reduce((sum, item) => sum + remainingQuantity(item), 0) || 0,
    productName: productMediaEntries[0]?.name || firstItem.product_name || 'No Product',
    productImage: productImages[0] || '',
    productImages: productImages,
    productMediaEntries,
    // What is left after items were cancelled on Shopify, else net payable.
    orderValue: remainingOrderValue(apiOrder) ?? netPayablePrice,
    financial_status: apiOrder.financial_status, // preserve raw API value
    // Shopify's current payment state (financial_status is creation-only).
    paymentStatus: paymentBadge(apiOrder),
    paymentProcessed: apiOrder.financial_status === 'paid',
    payout: netPayablePrice * 0.87, // Estimate 87% payout
    payoutStatus: apiOrder.financial_status === 'paid' ? 'PAID' : 'UNPAID',
    customerName: apiOrder.customer?.full_name || 'Unknown Customer',
    customerEmail: apiOrder.customer?.email || '',
    customerPhone: apiOrder.customer?.phone || '',
    pickupAddress: apiOrder.pickup_address?.address?.address_line_1 || 'No Address',
    businessName: apiOrder.vendor?.company_name || 'Unknown Business',
    shipTo: apiOrder.shipping_address?.address_line_1 || 'No Address',
    area: apiOrder.pickup_address?.address?.address_line_1 || '',
    city: apiOrder.shipping_address?.city || 'Unknown City',
    state: apiOrder.shipping_address?.state || 'Unknown State',
    pincode: apiOrder.shipping_address?.pincode || '',
    email: apiOrder.customer?.email || '',
    phone: apiOrder.customer?.phone || '',
    shippingCity: apiOrder.shipping_address?.city || 'Unknown City',
    shippingCountry: apiOrder.shipping_address?.country || 'India',
    length: parseInt(apiOrder.shipment?.dimensions?.length_cm || 0),
    breadth: parseInt(apiOrder.shipment?.dimensions?.width_cm || 0),
    height: parseInt(apiOrder.shipment?.dimensions?.height_cm || 0),
    weight: parseInt((parseFloat(apiOrder.shipment?.weight_kg || 0) * 1000).toFixed(0)),
    // Every leg raised for this order, newest first. The list payload has
    // carried these since the shipment history landed; the fulfillment column
    // pages through them while `shipment` stays the active one.
    shipper: apiOrder.shipment?.carrier?.name || 'Unknown Carrier',
    partner: apiOrder.shipment?.shipping_partner || 'Unknown Partner',
    labelGenerated: !!(apiOrder.shipment?.label_url && apiOrder.shipment?.label_generated_at),
    invoice: apiOrder.invoice || null,
    errors: 0, // Default since not in API
    orderStatus: statusMap[apiOrder.status] || 'New Orders',
    // Vendor-Shopify sync block, or null when this viewer doesn't see it.
    vendorShopifySync: apiOrder.vendor_shopify_sync || null,
    awbNumber: apiOrder.shipment?.awb_number || '',
    trackingLink: apiOrder.tracking_link || '',
    trackingUrl: `https://track.example.com/${apiOrder.shipment?.awb_number || ''}`,
    manifestId: null, // Not in API
    manifestDate: apiOrder.shipment?.label_generated_at ? new Date(apiOrder.shipment.label_generated_at) : null,
    shipperCode: apiOrder.shipment?.carrier?.code?.charAt(0) || 'U',
    deliveryStatus: apiOrder.shipment?.delivered_at ? 'Delivered' : (apiOrder.status === 'in_transit' ? 'In Transit' : ''),
    ndrReason: apiOrder.reason_label || null,
    reasonLabel: apiOrder.reason_label || null,
    createdAt: apiOrder.created_at,
    updatedAt: apiOrder.updated_at,
    shipment: apiOrder.shipment, // Preserve the entire shipment object for label_url access
    shipping_address: apiOrder.shipping_address, // Preserve for editing & display
    pickup_address: apiOrder.pickup_address, // Preserve for editing & display
    billing_address: apiOrder.billing_address,
    customer: apiOrder.customer,
    tags: apiOrder.tags?.tags || [],
    requiresPrescription: Boolean(apiOrder.requires_prescription),
    prescription: apiOrder.prescription || null,
    cancellation_reason: apiOrder.meta?.cancellation_reason || null,
    notes: apiOrder.notes || null,
    cancelledAt: apiOrder.meta?.cancelled_at || apiOrder.updated_at,
    confirmedAt: apiOrder.confirmed_at || null,
    deliveredAt: apiOrder.delivered_at || apiOrder.shipment?.delivered_at || null,
    deliveryDays: (apiOrder.confirmed_at && (apiOrder.delivered_at || apiOrder.shipment?.delivered_at)) 
      ? Math.round((new Date(apiOrder.delivered_at || apiOrder.shipment?.delivered_at) - new Date(apiOrder.confirmed_at)) / (1000 * 60 * 60 * 24))
      : '',
    processingTimeline: apiOrder.vendor?.processing_timeline || null,
    // Follow-up fields — normalize backend short names ('first','second','third') to long form
    followUpStage: { first: 'first_follow_up', second: 'second_follow_up', third: 'third_follow_up', exception: 'exception', penalty: 'penalty', none: 'none' }[apiOrder.follow_up_stage] || apiOrder.follow_up_stage || 'none',
    firstFollowupSent: apiOrder.first_followup_sent || false,
    secondFollowupSent: apiOrder.second_followup_sent || false,
    thirdFollowupSent: apiOrder.third_followup_sent || false,
    penaltySent: apiOrder.follow_up_stage === 'penalty',
    penaltyPercentage: apiOrder.penalty_percentage || 0,
    exceptionActive: apiOrder.exception_active || false,
    exceptionNote: apiOrder.exception_note || '',
    exceptionRaisedAt: apiOrder.exception_raised_at || null,
  };
}

export default ManageOrders;
